import { useState, useMemo } from 'react';
import { openPrintWindow } from '@/lib/print-document';
import type { ComponentType } from 'react';
import { RecordLink } from '@/components/records/RecordLink';
import { StockLedgerCheckCard } from '@/components/reports/StockLedgerCheckCard';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  TrendingUp, DollarSign, Package, ShoppingCart, CreditCard,
  ArrowUpRight, ArrowDownRight, BarChart3, FileText, Download,
  Printer, Eye, Receipt, Wallet, Calendar, ChevronLeft, ChevronRight, MapPin,
  Users, Star, LayoutGrid, Boxes, Truck, Building2, UserCheck,
  CheckCircle, XCircle, AlertCircle, AlertTriangle, Clock, Tag, RefreshCw, WifiOff,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, Line, LineChart,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatCard, type StatCardProps } from '@/components/ui/stat-card';
import { Button } from '@/components/ui/button';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { EmptyState } from '@/components/ui/empty-state';
import { PageHeader } from '@/components/ui/page-header';
import { AlertBanner } from '@/components/ui/alert-banner';
import { TabBar } from '@/components/ui/tab-bar';
import { Dialog } from 'primereact/dialog';
import { SearchInput } from '@/components/ui/search-input';
import api from '@/lib/api';
import { formatCurrency, formatDate, humanizeEnum } from '@/lib/utils';
import toast from 'react-hot-toast';
import { useLocationStore } from '@/stores/location.store';
import { useAuthStore } from '@/stores/auth.store';
import { useChartColors } from '@/lib/chart-colors';

// ---------------------------------------------------------------------------
// Report Data Interfaces
// ---------------------------------------------------------------------------

interface SaleReportRow {
  id: string;
  invoiceNumber: string;
  createdAt: string;
  status: string;
  subtotal?: number;
  discountAmount?: number;
  /** POS-I119: line (bargaining) discounts of the sale. */
  lineDiscountAmount?: number;
  taxAmount: number;
  totalAmount: number;
  customer?: { name: string } | null;
  payments?: { method: string; amount: number }[];
  items?: unknown[];
  _count?: { items: number };
}

interface LocationRow {
  id: string;
  name: string;
}

interface AbcAnalysisRow {
  productId: string;
  productName: string;
  revenue: number;
  revenueShare: number;
  unitsSold: number;
  abcClass: 'A' | 'B' | 'C';
}

interface ProductMarginRow {
  productId: string;
  productName: string;
  unitsSold: number;
  revenue: number;
  cogs: number;
  grossProfit: number;
  marginPct: number;
}

interface SupplierAgingRow {
  supplierId: string;
  supplierName: string;
  current: number;
  days30: number;
  days60: number;
  days90: number;
  over90: number;
  total: number;
}

interface PaymentMethodRow {
  method: string;
  transactionCount: number;
  totalAmount: number;
  avgAmount: number;
  percentageOfRevenue: number;
}

interface ExpenseCategoryRow {
  categoryName: string;
  totalAmount: number | string;
  count: number;
}

interface LowStockProductRow {
  id: string;
  name: string;
  sku?: string | null;
  unit?: string | null;
  stockQuantity: number;
}

interface ReturnsByTypeRow {
  returnType: string;
  count: number;
  totalRefund: number;
}

interface ReturnsTopProductRow {
  productName: string;
  totalQtyReturned: number;
  totalRefundValue: number;
}

interface CashierRow {
  userId: string;
  cashierName: string;
  salesCount: number;
  revenue: number;
  avgBasket: number;
  voidedCount: number;
}

interface ProductPerfRow {
  productId: string;
  productName: string;
  qtySold: number;
  revenue: number;
  totalCost: number;
  grossProfit: number;
  grossMarginPct: number;
}

interface HeatmapRow {
  dow: number;
  hour: number;
  salesCount: number;
  revenue: number;
}

interface CustomerReportRow {
  customerId: string;
  customerName: string;
  phone?: string | null;
  purchaseCount: number;
  totalSpent: number;
  avgBasket: number;
  lastPurchaseAt?: string | null;
}

interface SalesByCategoryRow {
  categoryId?: string | null;
  categoryName: string;
  revenue: number;
  qtySold?: number;
  transactions?: number;
  share?: number;
}

interface CustomerAnalyticsRow {
  customerId?: string | null;
  customerName: string;
  phone?: string | null;
  totalSpend?: number;
  totalSpent?: number;
  orderCount?: number;
  purchaseCount?: number;
  avgOrderValue?: number;
  avgBasket?: number;
  revenueShare?: number;
}

interface PromotionPerfRow {
  promotionId?: string | null;
  promotionName: string;
  orders?: number;
  revenue: number;
  discountGiven: number;
  avgOrderValue: number;
}

interface BalanceSheetAccountRow {
  accountName: string;
  balance: number;
}

interface TaxRateRow {
  taxName: string;
  taxRate: number;
  taxType: string;
  totalAmount: number;
  invoiceCount: number;
}

interface ExpenseTrendRow {
  categoryId?: string | null;
  categoryName: string;
  amount: number;
  monthly?: Record<string, string | number>[];
}

interface ValuationProductRow {
  productId: string;
  variantId?: string | null;
  productName: string;
  sku?: string | null;
  categoryName?: string | null;
  stockQuantity: number;
  costPrice: number;
  salePrice: number;
  stockValue: number;
  saleValue: number;
}

interface PurchaseSupplierRow {
  supplierId: string;
  supplierName: string;
  orderCount: number;
  receivedCount: number;
  pendingCount: number;
  cancelledCount: number;
  totalOrdered: number;
  totalReceived: number;
}

interface LocationReportRow {
  locationId: string;
  locationName: string;
  salesCount: number;
  revenue: number;
  avgBasket: number;
  taxCollected: number;
  voidedCount: number;
}

interface TurnoverRow {
  productId?: string | null;
  productName: string;
  sku?: string | null;
  unitsSold?: number;
  unitsReceived?: number;
  currentStock?: number;
  costValue: number;
  turnoverRatio?: number;
}

interface InvoiceTaxBreakdownRow {
  name: string;
  rate: number;
}

interface InvoiceItemRow {
  id: string;
  productName: string;
  productSku?: string | null;
  quantity: number;
  unitPrice: number;
  taxAmount: number;
  totalAmount: number;
  /** POS-I119: money knocked off this line (the line total is already net of it). */
  discountAmount?: number | string | null;
  discountType?: string | null;
  discountValue?: number | string | null;
  discountReason?: string | null;
  taxBreakdown: InvoiceTaxBreakdownRow[];
}

interface InvoicePaymentRow {
  method: string;
  amount: number;
  reference?: string | null;
}

interface CommissionRow {
  userId: string;
  staffName: string;
  email: string;
  commissionRate: number;
  salesCount: number;
  totalRevenue: number;
  commissionEarned: number;
  avgBasket: number;
}

interface TopSalesOfficerRow {
  userId: string;
  name: string;
  salesCount: number;
  salesTotal: number;
}

interface DefaulterRow {
  planId: string;
  customerName?: string | null;
  customerPhone?: string | null;
  invoiceNumber?: string | null;
  overdueCount: number;
  overdueAmount: number;
  remainingAmount: number;
}

interface OutstandingRow {
  customerId?: string | null;
  customerName?: string | null;
  customerPhone?: string | null;
  planCount: number;
  outstanding: number;
}

interface DeadStockRow {
  productId: string;
  productName: string;
  stockOnHand: number;
  stockValue: number;
  lastSold?: string | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
// Fixed categorical slot order for payment methods so the pie, legend dots and
// table chips all resolve to the same tokenized chart color for a given method.
const PAYMENT_ORDER: Record<string, number> = { CASH: 0, CARD: 1, BANK_TRANSFER: 2, MOBILE_WALLET: 3, CREDIT: 4 };
const PAYMENT_LABELS: Record<string, string> = { CASH: 'Cash', CARD: 'Card', BANK_TRANSFER: 'Bank Transfer', MOBILE_WALLET: 'Mobile Wallet', CREDIT: 'Credit' };
const tooltipFmt = (v: unknown) => formatCurrency(Number(v ?? 0));

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function downloadCSV(filename: string, headers: string[], rows: (string | number | boolean | null | undefined)[][]) {
  const esc = (v: unknown) => { const s = String(v ?? ''); return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s; };
  const csv = [headers.join(','), ...rows.map(r => r.map(esc).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

function printContent(title: string, html: string) {
  // POS-I240: one shared builder (base style, escaped title, Powered by footer on every page).
  openPrintWindow({
    title,
    heading: title,
    subtitle: `Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`,
    bodyHtml: html,
  });
}

function groupSalesByDay(sales: SaleReportRow[]): { date: string; revenue: number; tax: number; discount: number; count: number }[] {
  const map = new Map<string, { revenue: number; tax: number; discount: number; count: number }>();
  for (const s of sales) {
    const d = new Date(s.createdAt).toLocaleDateString('en-CA'); // YYYY-MM-DD
    const cur = map.get(d) || { revenue: 0, tax: 0, discount: 0, count: 0 };
    cur.revenue += Number(s.totalAmount || 0);
    cur.tax += Number(s.taxAmount || 0);
    cur.discount += Number(s.discountAmount || 0) + Number(s.lineDiscountAmount || 0);
    cur.count += 1;
    map.set(d, cur);
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, v]) => ({ date, ...v }));
}

function getPaymentBreakdown(sales: SaleReportRow[]): { method: string; amount: number; count: number }[] {
  const map = new Map<string, { amount: number; count: number }>();
  for (const s of sales) {
    for (const p of (s.payments || [])) {
      const m = p.method || 'CASH';
      const cur = map.get(m) || { amount: 0, count: 0 };
      cur.amount += Number(p.amount || 0);
      cur.count += 1;
      map.set(m, cur);
    }
  }
  return Array.from(map.entries()).map(([method, v]) => ({ method, ...v }));
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function ReportsPage() {
  const { t } = useTranslation('finance');
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') ?? 'overview';
  const setActiveTab = (tab: string) => setSearchParams({ tab });
  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const [salesPage, setSalesPage] = useState(1);
  const [salesSearch, setSalesSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const setPreset = (preset: string) => {
    const today = new Date();
    const fmt = (d: Date) => d.toISOString().split('T')[0];
    switch (preset) {
      case 'today':
        setDateFrom(fmt(today)); setDateTo(fmt(today)); break;
      case 'yesterday': {
        const y = new Date(today); y.setDate(y.getDate() - 1);
        setDateFrom(fmt(y)); setDateTo(fmt(y)); break;
      }
      case 'this-week': {
        const start = new Date(today); start.setDate(today.getDate() - today.getDay());
        setDateFrom(fmt(start)); setDateTo(fmt(today)); break;
      }
      case 'this-month': {
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        setDateFrom(fmt(start)); setDateTo(fmt(today)); break;
      }
      case 'last-30': {
        const d30 = new Date(today); d30.setDate(d30.getDate() - 30);
        setDateFrom(fmt(d30)); setDateTo(fmt(today)); break;
      }
    }
    setSalesPage(1);
  };


  const { selectedLocationId } = useLocationStore();
  const { hasPermission } = useAuthStore();
  const { series, seriesAt, grid, axis, status } = useChartColors();
  const paymentColor = (method: string) => {
    const idx = PAYMENT_ORDER[method];
    return idx === undefined ? status.muted : seriesAt(idx);
  };
  const canViewLocations = hasPermission('locations:*') || hasPermission('locations:read');
  const canViewInventory = hasPermission('inventory:*') || hasPermission('inventory:read');
  const canViewPurchases = hasPermission('purchases:*') || hasPermission('purchases:read');
  const canViewReports = hasPermission('reports:*') || hasPermission('reports:read') || hasPermission('accounting:*');
  const canViewFbr = hasPermission('fbr:*') || hasPermission('fbr:read');
  const canViewPromotions = hasPermission('promotions:*') || hasPermission('promotions:read');

  const { data: locationsData } = useQuery({
    queryKey: ['locations-list'],
    queryFn: () => api.get('/locations').then((r) => r.data),
    enabled: canViewLocations,
    staleTime: 60_000,
  });
  const locationsList: LocationRow[] = locationsData?.data ?? locationsData ?? [];

  // ── Queries ──
  const { data: pnl } = useQuery({
    queryKey: ['report-pnl', dateFrom, dateTo],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (dateFrom) qs.set('startDate', dateFrom);
      if (dateTo) qs.set('endDate', dateTo);
      const suffix = qs.toString();
      return (await api.get(`/reports/profit-loss${suffix ? `?${suffix}` : ''}`)).data;
    },
    enabled: !dateFrom || !dateTo || dateTo >= dateFrom,
  });
  const { data: balanceSheet } = useQuery({
    queryKey: ['report-bs'],
    queryFn: async () => (await api.get('/reports/balance-sheet')).data,
  });
  // Aggregates (exact totals + per-day trend) are computed server-side so they no
  // longer depend on how many raw rows we fetch below.
  const { data: salesSummary } = useQuery({
    queryKey: ['report-sales-summary', dateFrom, dateTo, selectedLocationId],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (dateFrom) qs.set('startDate', dateFrom);
      if (dateTo) qs.set('endDate', dateTo);
      if (selectedLocationId) qs.set('locationId', selectedLocationId);
      return (await api.get(`/sales/reports/summary?${qs}`)).data;
    },
    enabled: !dateFrom || !dateTo || dateTo >= dateFrom,
  });
  // The raw sales list now only feeds the paginated table, CSV export and the
  // payment-method pie. Capped at the list API max (500); a notice is shown when
  // truncated so the user can narrow the date range (exact totals above are unaffected).
  const salesQs = new URLSearchParams({ limit: '500' });
  if (dateFrom) salesQs.set('startDate', dateFrom);
  if (dateTo) salesQs.set('endDate', dateTo);
  if (selectedLocationId) salesQs.set('locationId', selectedLocationId);
  const { data: salesRaw } = useQuery({
    queryKey: ['report-sales', dateFrom, dateTo, selectedLocationId],
    queryFn: async () => (await api.get(`/sales?${salesQs}`)).data,
    enabled: !dateFrom || !dateTo || dateTo >= dateFrom,
  });
  const { data: products } = useQuery({
    queryKey: ['report-products-low'],
    queryFn: async () => (await api.get('/products?limit=10&sortBy=stockQuantity&sortOrder=asc')).data,
  });
  const { data: expenses } = useQuery({
    queryKey: ['report-expenses', dateFrom, dateTo],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (dateFrom) qs.set('startDate', dateFrom);
      if (dateTo) qs.set('endDate', dateTo);
      return (await api.get(`/expenses/summary?${qs}`)).data;
    },
  });
  const { data: invoiceDetail } = useQuery({
    queryKey: ['invoice-detail', invoiceId],
    queryFn: async () => (await api.get(`/sales/${invoiceId}`)).data,
    enabled: !!invoiceId,
  });

  // ── New analytics queries ──
  const buildQs = (extra?: Record<string, string>) => {
    const qs = new URLSearchParams();
    if (dateFrom) qs.set('startDate', dateFrom);
    if (dateTo) qs.set('endDate', dateTo);
    if (selectedLocationId) qs.set('locationId', selectedLocationId);
    if (extra) Object.entries(extra).forEach(([k, v]) => qs.set(k, v));
    return qs.toString();
  };

  const { data: cashierReport } = useQuery<CashierRow[]>({
    queryKey: ['report-cashier', dateFrom, dateTo, selectedLocationId],
    queryFn: async () => (await api.get(`/sales/reports/cashier?${buildQs()}`)).data,
    enabled: canViewReports,
  });
  const { data: productReport } = useQuery<ProductPerfRow[]>({
    queryKey: ['report-products-perf', dateFrom, dateTo, selectedLocationId],
    queryFn: async () => (await api.get(`/sales/reports/products?${buildQs({ limit: '20' })}`)).data,
    enabled: canViewReports,
  });
  const { data: heatmapReport } = useQuery<HeatmapRow[]>({
    queryKey: ['report-heatmap', dateFrom, dateTo, selectedLocationId],
    queryFn: async () => (await api.get(`/sales/reports/heatmap?${buildQs()}`)).data,
    enabled: canViewReports,
  });
  const { data: taxReport } = useQuery({
    queryKey: ['report-tax-summary', dateFrom, dateTo, selectedLocationId],
    queryFn: async () => (await api.get(`/sales/reports/tax-summary?${buildQs()}`)).data,
    enabled: canViewReports,
  });
  const { data: locationReport } = useQuery<LocationReportRow[]>({
    queryKey: ['report-by-location', dateFrom, dateTo],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (dateFrom) qs.set('startDate', dateFrom);
      if (dateTo) qs.set('endDate', dateTo);
      return (await api.get(`/sales/reports/by-location?${qs}`)).data;
    },
    enabled: canViewLocations && canViewReports,
  });
  const { data: customerReport } = useQuery<CustomerReportRow[]>({
    queryKey: ['report-customers', dateFrom, dateTo, selectedLocationId],
    queryFn: async () => (await api.get(`/sales/reports/customers?${buildQs({ limit: '20' })}`)).data,
    enabled: canViewReports,
  });
  const { data: inventoryValuation, isLoading: isLoadingInventoryValuation } = useQuery({
    queryKey: ['report-inventory-valuation', selectedLocationId],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (selectedLocationId) qs.set('locationId', selectedLocationId);
      return (await api.get(`/inventory/valuation?${qs}`)).data;
    },
    enabled: canViewInventory,
  });
  const { data: purchaseReport } = useQuery({
    queryKey: ['report-purchase-summary', dateFrom, dateTo],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (dateFrom) qs.set('startDate', dateFrom);
      if (dateTo) qs.set('endDate', dateTo);
      return (await api.get(`/purchases/reports/supplier-summary?${qs}`)).data;
    },
    enabled: canViewPurchases,
  });

  // ── New tab queries ──
  const invStart = dateFrom || new Date(Date.now() - 90 * 86400000).toISOString().split('T')[0];
  const invEnd = dateTo || new Date().toISOString().split('T')[0];

  const { data: inventoryTurnover, isLoading: isLoadingTurnover } = useQuery({
    queryKey: ['report-inventory-turnover', invStart, invEnd],
    queryFn: () => api.get(`/reports/inventory-turnover?startDate=${invStart}&endDate=${invEnd}`).then(r => r.data),
    enabled: activeTab === 'inventory' && canViewInventory,
  });
  const turnoverProducts = inventoryTurnover?.products ?? [];
  const { data: fbrSummary, isLoading: isLoadingFbr } = useQuery({
    queryKey: ['report-fbr-summary', invStart, invEnd],
    queryFn: () => api.get(`/reports/fbr-summary?startDate=${invStart}&endDate=${invEnd}`).then(r => r.data),
    enabled: activeTab === 'fbr' && canViewFbr,
  });
  const { data: customerAnalytics } = useQuery<CustomerAnalyticsRow[]>({
    queryKey: ['report-customer-analytics', dateFrom, dateTo, selectedLocationId],
    queryFn: () => api.get(`/reports/customer-analytics?${buildQs({ limit: '10' })}`).then(r => r.data),
    enabled: activeTab === 'analytics' && canViewReports,
  });
  const { data: salesByCategory } = useQuery<SalesByCategoryRow[]>({
    queryKey: ['report-sales-by-category', dateFrom, dateTo, selectedLocationId],
    queryFn: () => api.get(`/reports/sales-by-category?${buildQs()}`).then(r => r.data),
    enabled: activeTab === 'analytics' && canViewReports,
  });
  const { data: expenseTrend } = useQuery<ExpenseTrendRow[]>({
    queryKey: ['report-expense-trend', dateFrom, dateTo],
    queryFn: () => api.get(`/reports/expense-trend?${buildQs()}`).then(r => r.data),
    enabled: activeTab === 'finance' && canViewReports,
  });
  const { data: promotionsPerformance } = useQuery<PromotionPerfRow[]>({
    queryKey: ['report-promotions-performance', dateFrom, dateTo, selectedLocationId],
    queryFn: () => api.get(`/reports/promotions-performance?${buildQs()}`).then(r => r.data),
    enabled: activeTab === 'analytics' && canViewPromotions,
  });

  // ── ABC Analysis ──
  const { data: abcAnalysis, isLoading: isLoadingAbc } = useQuery({
    queryKey: ['report-abc-analysis'],
    queryFn: () => api.get('/inventory/abc-analysis').then(r => r.data),
    enabled: activeTab === 'abc' && canViewInventory,
  });
  const abcRows: AbcAnalysisRow[] = Array.isArray(abcAnalysis) ? abcAnalysis : (abcAnalysis?.data ?? []);

  // ── Product Margins ── (driven by the page-level date range; no separate control)
  const { data: productMargins, isLoading: isLoadingMargins } = useQuery({
    queryKey: ['report-product-margins', dateFrom, dateTo],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (dateFrom) qs.set('startDate', dateFrom);
      if (dateTo) qs.set('endDate', dateTo);
      return api.get(`/reports/product-margins?${qs.toString()}`).then(r => r.data);
    },
    enabled: activeTab === 'margins' && canViewReports && (!dateFrom || !dateTo || dateTo >= dateFrom),
  });
  const marginsRows: ProductMarginRow[] = Array.isArray(productMargins) ? productMargins : [];

  // ── Supplier Aging ──
  const { data: supplierAging, isLoading: isLoadingAging } = useQuery({
    queryKey: ['report-supplier-aging'],
    queryFn: () => api.get('/suppliers/aging').then(r => r.data),
    enabled: activeTab === 'supplier-aging' && canViewPurchases,
  });
  const agingRows: SupplierAgingRow[] = Array.isArray(supplierAging) ? supplierAging : [];

  // ── Payment Method Breakdown ──
  const { data: paymentMethodReport, isLoading: isLoadingPaymentMethods } = useQuery({
    queryKey: ['report-payment-methods', dateFrom, dateTo, selectedLocationId],
    queryFn: () => api.get(`/sales/reports/payment-methods?${buildQs()}`).then(r => r.data),
    enabled: activeTab === 'finance' && canViewReports,
  });
  const paymentMethodRows: PaymentMethodRow[] = Array.isArray(paymentMethodReport) ? paymentMethodReport : [];

  // ── Returns Summary ──
  const { data: returnsSummary, isLoading: isLoadingReturnsSummary } = useQuery({
    queryKey: ['report-returns-summary', dateFrom, dateTo, selectedLocationId],
    queryFn: () => api.get(`/sales/returns/report?${buildQs()}`).then(r => r.data),
    enabled: activeTab === 'sales' && canViewReports,
  });

  // ── Commission Report ──
  const [commissionUserId, _setCommissionUserId] = useState('');
  const { data: commissionReport, isLoading: isLoadingCommissions } = useQuery({
    queryKey: ['report-commissions', dateFrom, dateTo, selectedLocationId, commissionUserId],
    queryFn: () => {
      const qs = buildQs(commissionUserId ? { userId: commissionUserId } : {});
      return api.get(`/sales/reports/commissions?${qs}`).then(r => r.data);
    },
    enabled: activeTab === 'commissions' && canViewReports,
  });

  // ── MEIMS Recovery & BI (MEI-009/010/011) ──
  const canViewRecoveryBi =
    canViewReports || hasPermission('installments:*') || hasPermission('installments:read');
  const { data: recoveryKpis, isLoading: isLoadingRecoveryKpis, isError: isRecoveryKpisError } = useQuery({
    queryKey: ['report-recovery-kpis'],
    queryFn: () => api.get('/reports/recovery-kpis').then((r) => r.data),
    enabled: activeTab === 'recovery-bi' && canViewRecoveryBi,
  });
  const { data: installmentProfit, isLoading: isLoadingInstallmentProfit } = useQuery({
    queryKey: ['report-installment-profit'],
    queryFn: () => api.get('/reports/installment-profit').then((r) => r.data),
    enabled: activeTab === 'recovery-bi' && canViewRecoveryBi,
  });
  const { data: defaulterReport, isLoading: isLoadingDefaulters } = useQuery({
    queryKey: ['report-defaulters'],
    queryFn: () => api.get('/reports/defaulters').then((r) => r.data),
    enabled: activeTab === 'recovery-bi' && canViewRecoveryBi,
  });
  const { data: outstandingReport, isLoading: isLoadingOutstanding } = useQuery({
    queryKey: ['report-outstanding-balances'],
    queryFn: () => api.get('/reports/outstanding-balances').then((r) => r.data),
    enabled: activeTab === 'recovery-bi' && canViewRecoveryBi,
  });
  const { data: deadStockReport, isLoading: isLoadingDeadStock } = useQuery({
    queryKey: ['report-dead-stock'],
    queryFn: () => api.get('/reports/dead-stock?days=90').then((r) => r.data),
    enabled: activeTab === 'recovery-bi' && canViewRecoveryBi,
  });
  // ── Derived Data ──
  const allSales = useMemo<SaleReportRow[]>(() => salesRaw?.data || [], [salesRaw]);
  const summaryTotals = salesSummary?.totals;
  const totalSalesValue = summaryTotals ? Number(summaryTotals.revenue) : allSales.reduce((s, x) => s + Number(x.totalAmount || 0), 0);
  const totalTax = summaryTotals ? Number(summaryTotals.tax) : allSales.reduce((s, x) => s + Number(x.taxAmount || 0), 0);
  const totalDiscount = summaryTotals ? Number(summaryTotals.discount) : allSales.reduce((s, x) => s + Number(x.discountAmount || 0) + Number(x.lineDiscountAmount || 0), 0);
  const salesListTotal = Number(salesRaw?.meta?.total ?? 0);
  const totalSalesCount = summaryTotals ? Number(summaryTotals.count) : (salesListTotal || allSales.length);
  const avgSale = totalSalesCount > 0 ? totalSalesValue / totalSalesCount : 0;
  const totalExpenseAmount = Number(expenses?.totalAmount ?? 0);
  const salesTruncated = salesListTotal > allSales.length;

  const dailyData = useMemo(
    () => (salesSummary?.daily?.length ? salesSummary.daily : groupSalesByDay(allSales)),
    [salesSummary, allSales],
  );
  const paymentData = useMemo(() => getPaymentBreakdown(allSales), [allSales]);
  const expenseCategoryData = useMemo(() =>
    (expenses?.byCategory || []).map((c: ExpenseCategoryRow) => ({ name: c.categoryName, value: Number(c.totalAmount || 0), count: c.count })),
    [expenses],
  );

  // Heatmap grid (7 rows × 24 cols)
  const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const heatmapGrid = useMemo(() => {
    const cells = heatmapReport ?? [];
    const maxCount = Math.max(1, ...cells.map((r) => r.salesCount));
    return Array.from({ length: 7 }, (_, dow) =>
      Array.from({ length: 24 }, (_, hour) => {
        const cell = cells.find((r) => r.dow === dow && r.hour === hour);
        return { salesCount: cell?.salesCount ?? 0, revenue: cell?.revenue ?? 0, intensity: cell ? Math.sqrt(cell.salesCount / maxCount) : 0 };
      })
    );
  }, [heatmapReport]);

  // Filtered + paginated sales for table
  const filteredSales = useMemo(() => {
    if (!salesSearch.trim()) return allSales;
    const q = salesSearch.toLowerCase();
    return allSales.filter((s) =>
      (s.invoiceNumber || '').toLowerCase().includes(q) ||
      (s.customer?.name || '').toLowerCase().includes(q),
    );
  }, [allSales, salesSearch]);
  const SALES_PER_PAGE = 15;
  const totalSalesPages = Math.max(1, Math.ceil(filteredSales.length / SALES_PER_PAGE));
  const pagedSales = filteredSales.slice((salesPage - 1) * SALES_PER_PAGE, salesPage * SALES_PER_PAGE);

  // ── Export helpers ──
  function exportSalesCSV() {
    if (!allSales.length) return;
    downloadCSV('sales-report.csv',
      ['Invoice', 'Date', 'Customer', 'Items', 'Subtotal', 'Item discount', 'Discount', 'Tax', 'Total', 'Payment', 'Status'],
      allSales.map((s) => [
        s.invoiceNumber, new Date(s.createdAt).toLocaleDateString(), s.customer?.name || 'Walk-in',
        s._count?.items || s.items?.length || 0, Number(s.subtotal || 0), Number(s.lineDiscountAmount || 0), Number(s.discountAmount || 0),
        Number(s.taxAmount || 0), Number(s.totalAmount || 0),
        (s.payments || []).map((p) => PAYMENT_LABELS[p.method] || p.method).join('+'), s.status,
      ]),
    );
  }
  function printSalesReport() {
    if (!allSales.length) return;
    const rows = allSales.map((s) =>
      `<tr><td class="mono">${s.invoiceNumber}</td><td>${new Date(s.createdAt).toLocaleDateString()}</td><td>${s.customer?.name || 'Walk-in'}</td><td class="text-end mono">${formatCurrency(s.totalAmount)}</td><td>${s.status}</td></tr>`
    ).join('');
    printContent('Sales Report', `<table><thead><tr><th>Invoice</th><th>Date</th><th>Customer</th><th class="text-end">Total</th><th>Status</th></tr></thead><tbody>${rows}</tbody>
      <tfoot><tr class="total"><td colspan="3">Total (${allSales.length} invoices)</td><td class="text-end mono">${formatCurrency(totalSalesValue)}</td><td></td></tr></tfoot></table>`);
  }
  function printInvoice() {
    if (!invoiceDetail) return;
    const d = invoiceDetail;
    const itemRows = (d.items || []).map((it: InvoiceItemRow) =>
      `<tr><td>${it.productName}${Number(it.discountAmount) > 0 ? `<br/><small>Discount -${formatCurrency(Number(it.discountAmount))}${it.discountType === 'PERCENTAGE' ? ` (${Number(it.discountValue)}%)` : ''}${it.discountReason ? ` - ${it.discountReason}` : ''}</small>` : ''}</td><td>${it.productSku || ''}</td><td class="text-end">${it.quantity}</td><td class="text-end mono">${formatCurrency(it.unitPrice)}</td><td class="text-end mono">${formatCurrency(it.taxAmount)}</td><td class="text-end mono">${formatCurrency(it.totalAmount)}</td></tr>`
    ).join('');
    printContent(`Invoice ${d.invoiceNumber}`, `
      <div style="margin-bottom:16px;">
        <strong>Invoice:</strong> ${d.invoiceNumber}<br/>
        <strong>Date:</strong> ${new Date(d.createdAt).toLocaleString()}<br/>
        <strong>Customer:</strong> ${d.customer?.name || 'Walk-in Customer'}<br/>
        <strong>Status:</strong> ${d.status}
      </div>
      <table><thead><tr><th>Product</th><th>SKU</th><th class="text-end">Qty</th><th class="text-end">Price</th><th class="text-end">Tax</th><th class="text-end">Total</th></tr></thead>
      <tbody>${itemRows}</tbody>
      <tfoot>
        <tr><td colspan="5" class="text-end">Subtotal</td><td class="text-end mono">${formatCurrency(Number(d.subtotal) + Number(d.lineDiscountAmount || 0))}</td></tr>
        ${Number(d.lineDiscountAmount) > 0 ? `<tr><td colspan="5" class="text-end">Item discounts</td><td class="text-end mono">-${formatCurrency(d.lineDiscountAmount)}</td></tr>` : ''}
        <tr><td colspan="5" class="text-end">Discount</td><td class="text-end mono">-${formatCurrency(d.discountAmount)}</td></tr>
        <tr><td colspan="5" class="text-end">Tax</td><td class="text-end mono">${formatCurrency(d.taxAmount)}</td></tr>
        <tr class="total"><td colspan="5" class="text-end">Total</td><td class="text-end mono">${formatCurrency(d.totalAmount)}</td></tr>
      </tfoot></table>
      <div style="margin-top:12px;">
        <strong>Payment:</strong> ${(d.payments || []).map((p: InvoicePaymentRow) => `${PAYMENT_LABELS[p.method] || p.method}: ${formatCurrency(p.amount)}`).join(', ')}
      </div>`);
  }

  return (
    <div className="space-y-5 page-enter">

      {/* ── PAGE HEADER ── */}
      <PageHeader
        title={t('reportsAndAnalyticsTitle')}
        subtitle={t('reportsSubtitle')}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            {/* ABC analysis is a fixed trailing-90-day report (server side) — date controls do not apply (T-246). */}
            {activeTab !== 'abc' && (<>
            <div className="flex items-center gap-1 flex-wrap">
              {(['today', 'yesterday', 'this-week', 'this-month', 'last-30'] as const).map((preset) => (
                <button
                  key={preset}
                  onClick={() => setPreset(preset)}
                  className="rounded border border-border bg-background px-2 py-0.5 text-xs hover:bg-muted transition-colors"
                >
                  {preset === 'today' ? t('today') : preset === 'yesterday' ? t('yesterday') : preset === 'this-week' ? t('thisWeek') : preset === 'this-month' ? t('thisMonth') : t('last30Days')}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
              <input type="date" value={dateFrom} onChange={e => {
                const newDateFrom = e.target.value;
                setDateFrom(newDateFrom);
                setSalesPage(1);
                if (newDateFrom && dateTo && newDateFrom > dateTo) {
                  toast.error(t('startDateBeforeEndDate'));
                }
              }}
                className="rounded-lg border bg-transparent px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
              <span className="text-xs text-muted-foreground">{t('to')}</span>
              <input type="date" value={dateTo} onChange={e => {
                const newDateTo = e.target.value;
                setDateTo(newDateTo);
                setSalesPage(1);
                if (dateFrom && newDateTo && newDateTo < dateFrom) {
                  toast.error(t('endDateAfterStartDate'));
                }
              }}
                className="rounded-lg border bg-transparent px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
            </div>
            </>)}
            {canViewLocations && locationsList.length > 1 && (
              <div className="flex items-center gap-1.5 rounded-md border border-primary/30 bg-primary/10 px-2 py-1">
                <MapPin className="h-3.5 w-3.5 text-primary-text shrink-0" />
                <span className="text-xs font-medium text-primary-text">
                  {selectedLocationId
                    ? locationsList.find((l) => l.id === selectedLocationId)?.name ?? t('location')
                    : t('allLocations', { count: locationsList.length })}
                </span>
              </div>
            )}
          </div>
        }
      />

      {/* ── TAB NAVIGATION ── */}
      <div className="animate-fade-in-up -mx-1 border-b border-border/60 pb-1">
        <TabBar
          tabs={(
            [
              { id: 'overview',   label: t('overview'),          icon: BarChart3    },
              { id: 'sales',      label: t('salesTab'),          icon: ShoppingCart },
              { id: 'analytics',  label: t('analyticsTab'),      icon: TrendingUp   },
              { id: 'finance',    label: t('financeTab'),        icon: DollarSign   },
              { id: 'commissions', label: 'Commissions',         icon: UserCheck    },
              ...(canViewPurchases ? [{ id: 'purchases',      label: t('purchasesTab'),      icon: Truck      }] : []),
              ...(canViewLocations ? [{ id: 'locations',      label: t('locationsTab'),      icon: Building2  }] : []),
              ...(canViewInventory ? [{ id: 'inventory',      label: t('inventoryTab'),      icon: Boxes      }] : []),
              ...(canViewFbr       ? [{ id: 'fbr',            label: t('fbrTab'),            icon: Receipt    }] : []),
              ...(canViewInventory ? [{ id: 'abc',            label: t('abcAnalysisTab'),    icon: LayoutGrid }] : []),
              { id: 'margins',    label: t('profitMarginsTab'),  icon: TrendingUp   },
              ...(canViewPurchases ? [{ id: 'supplier-aging', label: t('supplierAgingTab'),  icon: Truck      }] : []),
              ...(canViewRecoveryBi ? [{ id: 'recovery-bi',   label: 'Recovery & BI',        icon: TrendingUp }] : []),
            ] as { id: string; label: string; icon: ComponentType<{ className?: string }> }[]
          ).map(tab => ({ key: tab.id, label: tab.label, icon: tab.icon }))}
          activeTab={activeTab}
          onTabChange={(key) => setActiveTab(key)}
        />
      </div>

      {/* ── Date range invalid warning ── */}
      {dateFrom && dateTo && dateTo < dateFrom && (
        <div className="flex items-center gap-2 rounded-xl border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning-text">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{t('invalidDateRange')}</span>
        </div>
      )}

      {/* ══════════════ TAB: OVERVIEW ══════════════ */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* KPI CARDS */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-6">
            <StatCard label={t('totalRevenue')} value={totalSalesValue} format="currency" helper={t('invoicesCount', { count: totalSalesCount })} icon={DollarSign} accent="success" />
            <StatCard label={t('avgSale')} value={avgSale} format="currency" icon={BarChart3} accent="info" />
            <StatCard label={t('totalTax')} value={totalTax} format="currency" icon={Receipt} accent="warning" />
            <StatCard label={t('totalDiscounts')} value={totalDiscount} format="currency" icon={CreditCard} />
            <StatCard
              label={t('netIncome')}
              value={
                <span className={pnl?.netIncome !== undefined && Number(pnl.netIncome) < 0 ? 'text-destructive-text' : 'text-success-text'}>
                  {formatCurrency(pnl?.netIncome ?? 0)}
                </span>
              }
              format="raw"
              icon={TrendingUp}
              accent={pnl?.netIncome !== undefined && Number(pnl.netIncome) < 0 ? 'danger' : 'success'}
            />
            <StatCard label={t('expenses')} value={totalExpenseAmount} format="currency" icon={Wallet} accent="danger" />
          </div>

          {/* REVENUE TREND */}
          {dailyData.length > 0 && (
            <Card className="animate-fade-in-up">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2"><TrendingUp className="h-5 w-5" /> {t('revenueTrend')}</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={dailyData} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                    <defs>
                      <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={series[0]} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={series[0]} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                    <XAxis stroke={axis} dataKey="date" tick={{ fontSize: 11, fill: axis }} />
                    <YAxis stroke={axis} tick={{ fontSize: 11, fill: axis }} />
                    <Tooltip formatter={tooltipFmt} />
                    <Legend />
                    <Area type="monotone" dataKey="revenue" name="Revenue" stroke={series[0]} fill="url(#revGrad)" strokeWidth={2} />
                    <Line type="monotone" dataKey="tax" name="Tax" stroke={series[1]} strokeWidth={1.5} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* PAYMENT / EXPENSE / VOLUME */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {paymentData.length > 0 && (
              <Card className="animate-fade-in-up">
                <CardHeader><CardTitle className="text-lg flex items-center gap-2"><CreditCard className="h-5 w-5" /> {t('paymentMethods')}</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={paymentData.map(p => ({ name: PAYMENT_LABELS[p.method] || p.method, value: p.amount }))}
                        cx="50%" cy="50%" outerRadius={90} innerRadius={50} dataKey="value" paddingAngle={3}
                        label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`} labelLine={false}>
                        {paymentData.map((p, i) => <Cell key={i} fill={paymentColor(p.method)} />)}
                      </Pie>
                      <Tooltip formatter={tooltipFmt} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-2 space-y-1">
                    {paymentData.map(p => (
                      <div key={p.method} className="flex items-center justify-between text-xs">
                        <span className="flex items-center gap-1.5">
                          <span className="h-2 w-2 rounded-full" style={{ background: paymentColor(p.method) }} />
                          {PAYMENT_LABELS[p.method] || p.method}
                        </span>
                        <span className="font-mono">{formatCurrency(p.amount)} <span className="text-muted-foreground">({p.count})</span></span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            {expenseCategoryData.length > 0 && (
              <Card className="animate-fade-in-up">
                <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Wallet className="h-5 w-5" /> {t('expensesByCategory')}</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={expenseCategoryData} layout="vertical" margin={{ top: 5, right: 20, bottom: 5, left: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                      <XAxis stroke={axis} type="number" tick={{ fontSize: 11, fill: axis }} />
                      <YAxis stroke={axis} dataKey="name" type="category" tick={{ fontSize: 11, fill: axis }} width={55} />
                      <Tooltip formatter={tooltipFmt} />
                      <Bar dataKey="value" name="Amount" fill={series[0]} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="mt-2 flex items-center justify-between border-t pt-2 font-medium text-sm">
                    <span>{t('totalExpenses')}</span>
                    <span className="text-destructive-text font-mono">{formatCurrency(totalExpenseAmount)}</span>
                  </div>
                </CardContent>
              </Card>
            )}
            {dailyData.length > 0 && (
              <Card className="animate-fade-in-up">
                <CardHeader><CardTitle className="text-lg flex items-center gap-2"><ShoppingCart className="h-5 w-5" /> {t('salesVolume')}</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={dailyData} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                      <XAxis stroke={axis} dataKey="date" tick={{ fontSize: 10, fill: axis }} />
                      <YAxis stroke={axis} tick={{ fontSize: 11, fill: axis }} allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="count" name="Transactions" fill={series[0]} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* ══════════════ TAB: SALES ══════════════ */}
      {activeTab === 'sales' && (
        <div className="space-y-6">
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" size="sm" onClick={exportSalesCSV}><Download className="me-1 h-3.5 w-3.5" />{t('exportCsv')}</Button>
            <Button variant="outline" size="sm" onClick={printSalesReport}><Printer className="me-1 h-3.5 w-3.5" />{t('print')}</Button>
          </div>

          {/* Sales ledger */}
          <Card className="animate-fade-in-up">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <CardTitle className="text-lg flex items-center gap-2"><ShoppingCart className="h-5 w-5" /> {t('salesLedger')}</CardTitle>
                <SearchInput
                  value={salesSearch}
                  onChange={(v) => { setSalesSearch(v); setSalesPage(1); }}
                  placeholder={t('searchInvoices')}
                  className="w-64"
                />
              </div>
            </CardHeader>
            <CardContent>
              {salesTruncated && (
                <div className="mb-3 flex items-center gap-2 rounded-lg border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning-text">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  Showing the first {allSales.length.toLocaleString()} of {salesListTotal.toLocaleString()} invoices. Totals above are exact; narrow the date range to list every invoice here.
                </div>
              )}
              {!filteredSales.length ? (
                <p className="py-8 text-center text-sm text-muted-foreground">{t('noSalesFound')}</p>
              ) : (
                <>
                  <div className="overflow-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-muted-foreground">
                          <th className="pb-2 text-start font-medium">{t('invoice')}</th>
                          <th className="pb-2 text-start font-medium">{t('date')}</th>
                          <th className="pb-2 text-start font-medium">{t('customer')}</th>
                          <th className="pb-2 text-end font-medium">{t('items')}</th>
                          <th className="pb-2 text-end font-medium">{t('tax')}</th>
                          <th className="pb-2 text-end font-medium">{t('total')}</th>
                          <th className="pb-2 text-start font-medium">{t('payment')}</th>
                          <th className="pb-2 text-center font-medium">{t('status')}</th>
                          <th className="pb-2 text-center font-medium w-10"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {pagedSales.map((s) => (
                          <tr key={s.id} className="hover:bg-accent/50 transition-colors duration-150">
                            <td className="py-2.5 text-xs font-medium"><RecordLink type="invoice" id={s.id} label={s.invoiceNumber} variant="mono" className="text-xs" /></td>
                            <td className="py-2.5 text-xs text-muted-foreground">{new Date(s.createdAt).toLocaleDateString()}</td>
                            <td className="py-2.5 text-xs">{s.customer?.name || <span className="text-muted-foreground">{t('walkIn')}</span>}</td>
                            <td className="py-2.5 text-end text-xs">{s._count?.items ?? s.items?.length ?? 0}</td>
                            <td className="py-2.5 text-end font-mono text-xs">{formatCurrency(s.taxAmount)}</td>
                            <td className="py-2.5 text-end font-mono text-xs font-medium">{formatCurrency(s.totalAmount)}</td>
                            <td className="py-2.5 text-xs">
                              <div className="flex gap-1 flex-wrap">
                                {(s.payments || []).map((p, i: number) => (
                                  <span key={i} className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium text-foreground border" style={{ borderColor: paymentColor(p.method) }}>
                                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: paymentColor(p.method) }} aria-hidden="true" />
                                    {PAYMENT_LABELS[p.method] || p.method}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="py-2.5 text-center">
                              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
                                s.status === 'COMPLETED' ? 'bg-success/10 text-success-text' : 'bg-destructive/10 text-destructive-text'
                              }`}>{s.status}</span>
                            </td>
                            <td className="py-2.5 text-center">
                              <button onClick={() => setInvoiceId(s.id)} className="rounded-lg p-1 hover:bg-accent transition-colors" title={t('viewInvoice')}>
                                <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                    <span>{t('showingEntries', { from: (salesPage - 1) * SALES_PER_PAGE + 1, to: Math.min(salesPage * SALES_PER_PAGE, filteredSales.length), total: filteredSales.length })}</span>
                    <div className="flex gap-1">
                      <button disabled={salesPage <= 1} onClick={() => setSalesPage(p => p - 1)} className="rounded-md border px-2 py-1 hover:bg-accent disabled:opacity-40 transition-colors"><ChevronLeft className="h-3.5 w-3.5" /></button>
                      <span className="px-2 py-1">{t('pageOf', { page: salesPage, total: totalSalesPages })}</span>
                      <button disabled={salesPage >= totalSalesPages} onClick={() => setSalesPage(p => p + 1)} className="rounded-md border px-2 py-1 hover:bg-accent disabled:opacity-40 transition-colors"><ChevronRight className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Low Stock Alert */}
          <Card className="animate-fade-in-up">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><Package className="h-5 w-5" /> {t('lowStockAlert')}</CardTitle>
            </CardHeader>
            <CardContent>
              {!products?.data?.length ? (
                <p className="py-6 text-center text-sm text-muted-foreground">{t('allProductsInStock')}</p>
              ) : (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                  {products.data.map((p: LowStockProductRow) => (
                    <div key={p.id} className="flex items-center justify-between rounded-lg border p-3 hover:bg-accent/50 transition-colors">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{p.name}</p>
                        <p className="text-[11px] text-muted-foreground font-mono">{p.sku || t('noSku')}</p>
                      </div>
                      <span className={`ms-2 text-sm font-bold whitespace-nowrap ${p.stockQuantity <= 0 ? 'text-destructive-text' : p.stockQuantity <= 10 ? 'text-warning-text' : 'text-success-text'}`}>
                        {p.stockQuantity} {p.unit || 'pcs'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Returns & Refunds Summary */}
          <Card className="animate-fade-in-up">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2"><RefreshCw className="h-5 w-5 text-destructive-text" /> Returns & Refunds Summary</CardTitle>
                  <p className="text-xs text-muted-foreground mt-0.5">Refund volumes, types and most-returned products</p>
                </div>
                {returnsSummary?.topReturnedProducts?.length > 0 && (
                  <Button variant="outline" size="sm" onClick={() => downloadCSV('returns-summary.csv',
                    ['Product', 'Qty Returned', 'Refund Value'],
                    (returnsSummary.topReturnedProducts ?? []).map((r: ReturnsTopProductRow) => [r.productName, r.totalQtyReturned, r.totalRefundValue.toFixed(2)])
                  )}>
                    <Download className="h-4 w-4 me-1" /> CSV
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingReturnsSummary ? (
                <SkeletonTable rows={3} cols={3} />
              ) : !returnsSummary || returnsSummary.totals?.returnCount === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No returns recorded for this period.</p>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="rounded-lg border bg-muted/30 p-3 text-center">
                      <div className="text-xs text-muted-foreground mb-1">Total Returns</div>
                      <div className="text-2xl font-bold text-destructive-text">{returnsSummary.totals.returnCount}</div>
                    </div>
                    <div className="rounded-lg border bg-muted/30 p-3 text-center">
                      <div className="text-xs text-muted-foreground mb-1">Total Refunded</div>
                      <div className="text-2xl font-bold text-destructive-text">{formatCurrency(returnsSummary.totals.totalRefund)}</div>
                    </div>
                    <div className="rounded-lg border bg-muted/30 p-3 text-center">
                      <div className="text-xs text-muted-foreground mb-1">Avg. Refund</div>
                      <div className="text-2xl font-bold">{formatCurrency(returnsSummary.totals.avgRefund)}</div>
                    </div>
                  </div>
                  {returnsSummary.byType?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {returnsSummary.byType.map((t: ReturnsByTypeRow) => (
                        <div key={t.returnType} className="flex-1 min-w-[120px] rounded-lg border bg-muted/30 p-2 text-center">
                          <div className="text-xs text-muted-foreground uppercase tracking-wide mb-0.5">{t.returnType.replace('_', ' ')}</div>
                          <div className="font-semibold">{t.count} returns</div>
                          <div className="text-xs text-destructive-text">{formatCurrency(t.totalRefund)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {returnsSummary.topReturnedProducts?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Top Returned Products</p>
                      <table className="w-full text-sm">
                        <thead><tr className="border-b">
                          <th className="px-4 py-2 text-start text-xs font-semibold uppercase tracking-wide text-muted-foreground">Product</th>
                          <th className="px-4 py-2 text-end text-xs font-semibold uppercase tracking-wide text-muted-foreground">Qty Returned</th>
                          <th className="px-4 py-2 text-end text-xs font-semibold uppercase tracking-wide text-muted-foreground">Refund Value</th>
                        </tr></thead>
                        <tbody className="divide-y">
                          {returnsSummary.topReturnedProducts.map((p: ReturnsTopProductRow) => (
                            <tr key={p.productName} className="hover:bg-muted/50">
                              <td className="px-4 py-2">{p.productName}</td>
                              <td className="px-4 py-2 text-end font-mono">{p.totalQtyReturned}</td>
                              <td className="px-4 py-2 text-end font-mono text-destructive-text">{formatCurrency(p.totalRefundValue)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ══════════════ TAB: ANALYTICS ══════════════ */}
      {activeTab === 'analytics' && (
        <div className="space-y-6">
          {/* Cashier Performance */}
          {cashierReport && cashierReport.length > 0 ? (
            <Card className="animate-fade-in-up">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-lg flex items-center gap-2"><Users className="h-5 w-5 text-info-text" /> {t('cashierPerformance')}</CardTitle>
                  <Button variant="outline" size="sm" onClick={() => downloadCSV('cashier-performance.csv',
                    ['Cashier', 'Sales Count', 'Revenue', 'Avg Basket', 'Voids'],
                    (cashierReport ?? []).map((r) => [r.cashierName, r.salesCount, Number(r.revenue).toFixed(2), Number(r.avgBasket).toFixed(2), r.voidedCount])
                  )}><Download className="me-1 h-3.5 w-3.5" />{t('csv')}</Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={cashierReport} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                    <XAxis stroke={axis} dataKey="cashierName" tick={{ fontSize: 11, fill: axis }} />
                    <YAxis stroke={axis} tick={{ fontSize: 11, fill: axis }} />
                    <Tooltip formatter={tooltipFmt} />
                    <Bar dataKey="revenue" name={t('revenue')} fill={series[0]} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                <div className="overflow-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b text-muted-foreground">
                      <th className="pb-2 text-start font-medium">{t('cashier')}</th>
                      <th className="pb-2 text-end font-medium">{t('salesCount')}</th>
                      <th className="pb-2 text-end font-medium">{t('revenue')}</th>
                      <th className="pb-2 text-end font-medium">{t('avgBasket')}</th>
                      <th className="pb-2 text-end font-medium">Voids</th>
                    </tr></thead>
                    <tbody className="divide-y">
                      {cashierReport.map((r) => (
                        <tr key={r.userId} className="hover:bg-accent/50">
                          <td className="py-2 font-medium">{r.cashierName}</td>
                          <td className="py-2 text-end">{r.salesCount}</td>
                          <td className="py-2 text-end font-mono">{formatCurrency(r.revenue)}</td>
                          <td className="py-2 text-end font-mono">{formatCurrency(r.avgBasket)}</td>
                          <td className="py-2 text-end text-destructive-text">{r.voidedCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">{t('noCashierData')}</CardContent></Card>
          )}

          {/* Product Performance */}
          {productReport && productReport.length > 0 && (
            <Card className="animate-fade-in-up">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-lg flex items-center gap-2"><Star className="h-5 w-5 text-warning-text" /> {t('productPerformance')}</CardTitle>
                  <Button variant="outline" size="sm" onClick={() => downloadCSV('product-performance.csv',
                    ['Product', 'Qty Sold', 'Revenue', 'Total Cost', 'Gross Profit', 'Margin %'],
                    (productReport ?? []).map((r) => [r.productName, r.qtySold, Number(r.revenue).toFixed(2), Number(r.totalCost).toFixed(2), Number(r.grossProfit).toFixed(2), Number(r.grossMarginPct).toFixed(1) + '%'])
                  )}><Download className="me-1 h-3.5 w-3.5" />{t('csv')}</Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b text-muted-foreground">
                      <th className="pb-2 text-start font-medium">{t('product')}</th>
                      <th className="pb-2 text-end font-medium">{t('qtySold')}</th>
                      <th className="pb-2 text-end font-medium">{t('revenue')}</th>
                      <th className="pb-2 text-end font-medium">{t('cost')}</th>
                      <th className="pb-2 text-end font-medium">{t('grossProfit')}</th>
                      <th className="pb-2 text-end font-medium">{t('marginPct')}</th>
                    </tr></thead>
                    <tbody className="divide-y">
                      {productReport.map((r, i: number) => (
                        <tr key={r.productId} className="hover:bg-accent/50">
                          <td className="py-2">
                            <span className={`me-2 text-[10px] font-bold ${i === 0 ? 'text-warning-text' : 'text-muted-foreground'}`}>#{i + 1}</span>{' '}
                            {r.productName}
                          </td>
                          <td className="py-2 text-end">{r.qtySold}</td>
                          <td className="py-2 text-end font-mono">{formatCurrency(r.revenue)}</td>
                          <td className="py-2 text-end font-mono text-muted-foreground">{formatCurrency(r.totalCost)}</td>
                          <td className="py-2 text-end font-mono text-success-text">{formatCurrency(r.grossProfit)}</td>
                          <td className="py-2 text-end">
                            <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold ${r.grossMarginPct >= 30 ? 'bg-success/10 text-success-text' : r.grossMarginPct >= 15 ? 'bg-warning/10 text-warning-text' : 'bg-destructive/10 text-destructive-text'}`}>
                              {Number(r.grossMarginPct).toFixed(1)}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Hourly Heatmap */}
          {heatmapReport && heatmapReport.length > 0 && (
            <Card className="animate-fade-in-up">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2"><LayoutGrid className="h-5 w-5 text-primary-text" /> {t('hourlySalesHeatmap')} <span className="text-xs text-muted-foreground font-normal">({t('darkerMoreSales')})</span></CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-auto">
                  <div className="min-w-[680px]">
                    <div className="flex ms-10 mb-1">
                      {Array.from({ length: 24 }, (_, h) => (
                        <div key={h} className="flex-1 text-center text-[9px] text-muted-foreground leading-tight">{h === 0 ? '12a' : h < 12 ? `${h}a` : h === 12 ? '12p' : `${h - 12}p`}</div>
                      ))}
                    </div>
                    {heatmapGrid.map((row, dow) => (
                      <div key={dow} className="flex items-center gap-0 mb-0.5">
                        <div className="w-10 text-[10px] font-medium text-end pe-1.5 text-muted-foreground shrink-0">{DOW_LABELS[dow]}</div>
                        {row.map((cell, hour) => (
                          <div key={hour} className="flex-1 aspect-square rounded-sm mx-px"
                            style={{ backgroundColor: `rgba(99,102,241,${cell.intensity})` }}
                            title={`${DOW_LABELS[dow]} ${hour}:00 — ${cell.salesCount} sales, ${formatCurrency(cell.revenue)}`}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Customer Analytics */}
          {customerReport && customerReport.length > 0 && (
            <Card className="animate-fade-in-up">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-lg flex items-center gap-2"><UserCheck className="h-5 w-5 text-success-text" /> {t('customerAnalytics')}</CardTitle>
                  <Button variant="outline" size="sm" onClick={() => downloadCSV('customer-analytics.csv',
                    ['Customer', 'Phone', 'Purchases', 'Total Spent', 'Avg Basket', 'Last Purchase'],
                    (customerReport ?? []).map((r) => [r.customerName, r.phone || '', r.purchaseCount, Number(r.totalSpent).toFixed(2), Number(r.avgBasket).toFixed(2), r.lastPurchaseAt ? new Date(r.lastPurchaseAt).toLocaleDateString() : ''])
                  )}><Download className="me-1 h-3.5 w-3.5" />{t('csv')}</Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b text-muted-foreground">
                      <th className="pb-2 text-start font-medium">{t('customer')}</th>
                      <th className="pb-2 text-start font-medium">{t('phone')}</th>
                      <th className="pb-2 text-end font-medium">{t('purchasesCount')}</th>
                      <th className="pb-2 text-end font-medium">{t('totalSpent')}</th>
                      <th className="pb-2 text-end font-medium">{t('avgBasket')}</th>
                      <th className="pb-2 text-end font-medium">{t('lastVisit')}</th>
                    </tr></thead>
                    <tbody className="divide-y">
                      {customerReport.map((r, i: number) => (
                        <tr key={r.customerId} className="hover:bg-accent/50">
                          <td className="py-2 font-medium">
                            <span className="me-1.5 text-[10px] font-bold text-muted-foreground">#{i + 1}</span>{' '}{r.customerName}
                          </td>
                          <td className="py-2 text-xs text-muted-foreground">{r.phone || '—'}</td>
                          <td className="py-2 text-end">{r.purchaseCount}</td>
                          <td className="py-2 text-end font-mono text-success-text font-medium">{formatCurrency(r.totalSpent)}</td>
                          <td className="py-2 text-end font-mono">{formatCurrency(r.avgBasket)}</td>
                          <td className="py-2 text-end text-xs text-muted-foreground">{r.lastPurchaseAt ? new Date(r.lastPurchaseAt).toLocaleDateString() : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Sales by Category */}
          {salesByCategory && salesByCategory.length > 0 && (
            <Card className="animate-fade-in-up">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-lg flex items-center gap-2"><Tag className="h-5 w-5 text-primary-text" /> {t('salesByCategory')}</CardTitle>
                  <Button variant="outline" size="sm" onClick={() => downloadCSV('sales-by-category.csv',
                    ['Category', 'Revenue', 'Qty Sold', 'Transactions', 'Share %'],
                    (salesByCategory ?? []).map((r) => [r.categoryName, Number(r.revenue).toFixed(2), r.qtySold, r.transactions, Number(r.share ?? 0).toFixed(1) + '%'])
                  )}><Download className="me-1 h-3.5 w-3.5" />{t('csv')}</Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={salesByCategory.map((r) => ({ name: r.categoryName, value: Number(r.revenue ?? 0) }))}
                      cx="50%" cy="50%" outerRadius={95} innerRadius={50}
                      dataKey="value" paddingAngle={3}
                      label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {salesByCategory.map((_, i: number) => <Cell key={i} fill={seriesAt(i)} />)}
                    </Pie>
                    <Tooltip formatter={tooltipFmt} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="overflow-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b text-muted-foreground">
                      <th className="pb-2 text-start font-medium">{t('category')}</th>
                      <th className="pb-2 text-end font-medium">{t('revenue')}</th>
                      <th className="pb-2 text-end font-medium">{t('qtySold')}</th>
                      <th className="pb-2 text-end font-medium">{t('transactions')}</th>
                      <th className="pb-2 text-end font-medium">{t('sharePercent')}</th>
                    </tr></thead>
                    <tbody className="divide-y">
                      {salesByCategory.map((r, i: number) => (
                        <tr key={r.categoryId ?? r.categoryName} className="hover:bg-accent/50">
                          <td className="py-2 font-medium flex items-center gap-2">
                            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: seriesAt(i) }} />
                            {r.categoryName}
                          </td>
                          <td className="py-2 text-end font-mono text-success-text">{formatCurrency(r.revenue)}</td>
                          <td className="py-2 text-end">{r.qtySold ?? 0}</td>
                          <td className="py-2 text-end">{r.transactions ?? 0}</td>
                          <td className="py-2 text-end">
                            <span className="inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold bg-primary/10 text-primary-text">
                              {Number(r.share ?? 0).toFixed(1)}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Customer Revenue Analysis (from /reports/customer-analytics) */}
          {customerAnalytics && customerAnalytics.length > 0 && (
            <Card className="animate-fade-in-up">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-lg flex items-center gap-2"><UserCheck className="h-5 w-5 text-primary-text" /> {t('customerRevenueAnalysis')} <span className="text-xs font-normal text-muted-foreground">({t('top10')})</span></CardTitle>
                  <Button variant="outline" size="sm" onClick={() => downloadCSV('customer-analytics.csv',
                    ['Rank', 'Customer', 'Phone', 'Total Spend', 'Orders', 'Avg Order Value', 'Revenue Share %'],
                    (customerAnalytics ?? []).map((r, i: number) => [i + 1, r.customerName, r.phone || '', Number(r.totalSpend ?? r.totalSpent ?? 0).toFixed(2), r.orderCount ?? r.purchaseCount ?? 0, Number(r.avgOrderValue ?? r.avgBasket ?? 0).toFixed(2), Number(r.revenueShare ?? 0).toFixed(1) + '%'])
                  )}><Download className="me-1 h-3.5 w-3.5" />{t('csv')}</Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b text-muted-foreground">
                      <th className="pb-2 text-start font-medium w-10">{t('rank')}</th>
                      <th className="pb-2 text-start font-medium">{t('customer')}</th>
                      <th className="pb-2 text-start font-medium">{t('phone')}</th>
                      <th className="pb-2 text-end font-medium">{t('totalSpend')}</th>
                      <th className="pb-2 text-end font-medium">{t('orders')}</th>
                      <th className="pb-2 text-end font-medium">{t('avgOrder')}</th>
                      <th className="pb-2 text-end font-medium">{t('revenueShare')}</th>
                    </tr></thead>
                    <tbody className="divide-y">
                      {customerAnalytics.map((r, i: number) => (
                        <tr key={r.customerId ?? r.customerName} className="hover:bg-accent/50">
                          <td className="py-2 text-xs font-bold text-muted-foreground">#{i + 1}</td>
                          <td className="py-2 font-medium">{r.customerName}</td>
                          <td className="py-2 text-xs text-muted-foreground">{r.phone || '—'}</td>
                          <td className="py-2 text-end font-mono text-success-text font-medium">{formatCurrency(r.totalSpend ?? r.totalSpent ?? 0)}</td>
                          <td className="py-2 text-end">{r.orderCount ?? r.purchaseCount ?? 0}</td>
                          <td className="py-2 text-end font-mono">{formatCurrency(r.avgOrderValue ?? r.avgBasket ?? 0)}</td>
                          <td className="py-2 text-end">
                            <span className="inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold bg-primary/10 text-primary-text">
                              {Number(r.revenueShare ?? 0).toFixed(1)}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Promotions Performance */}
          {canViewPromotions && promotionsPerformance && promotionsPerformance.length > 0 && (
            <Card className="animate-fade-in-up">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-lg flex items-center gap-2"><Tag className="h-5 w-5 text-primary-text" /> {t('promotionsPerformance')}</CardTitle>
                  <Button variant="outline" size="sm" onClick={() => downloadCSV('promotions-performance.csv',
                    ['Promotion', 'Orders', 'Revenue', 'Discount Given', 'Avg Order Value'],
                    (promotionsPerformance ?? []).map((r) => [r.promotionName, r.orders ?? 0, Number(r.revenue ?? 0).toFixed(2), Number(r.discountGiven ?? 0).toFixed(2), Number(r.avgOrderValue ?? 0).toFixed(2)])
                  )}><Download className="me-1 h-3.5 w-3.5" />{t('csv')}</Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b text-muted-foreground">
                      <th className="pb-2 text-start font-medium">{t('promotion')}</th>
                      <th className="pb-2 text-end font-medium">{t('orders')}</th>
                      <th className="pb-2 text-end font-medium">{t('revenue')}</th>
                      <th className="pb-2 text-end font-medium">{t('discountGiven')}</th>
                      <th className="pb-2 text-end font-medium">{t('avgOrderValue')}</th>
                    </tr></thead>
                    <tbody className="divide-y">
                      {promotionsPerformance.map((r) => (
                        <tr key={r.promotionId ?? r.promotionName} className="hover:bg-accent/50">
                          <td className="py-2 font-medium">{r.promotionName}</td>
                          <td className="py-2 text-end">{r.orders ?? 0}</td>
                          <td className="py-2 text-end font-mono text-success-text">{formatCurrency(r.revenue)}</td>
                          <td className="py-2 text-end font-mono text-destructive-text">{formatCurrency(r.discountGiven)}</td>
                          <td className="py-2 text-end font-mono">{formatCurrency(r.avgOrderValue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* ══════════════ TAB: FINANCE ══════════════ */}
      {activeTab === 'finance' && (
        <div className="space-y-6">
          {/* P&L + Balance Sheet */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {pnl && (
              <Card className="animate-fade-in-up">
                <CardHeader><CardTitle className="text-lg flex items-center gap-2"><TrendingUp className="h-5 w-5" /> {t('pnlSummary')}</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <PnlRow label={t('revenueExTax')} value={pnl.revenue?.total ?? 0} color="text-success-text" />
                    <PnlRow label={t('costOfGoods')} value={pnl.costOfGoods?.total ?? 0} color="text-warning-text" />
                    <div className="border-t pt-2"><PnlRow label={t('grossProfit')} value={pnl.grossProfit ?? 0} bold /></div>
                    <PnlRow label={t('operatingExpenses')} value={-(pnl.operatingExpenses?.total ?? 0)} color="text-destructive-text" />
                    <div className="rounded-lg bg-gradient-to-r from-primary/10 to-primary/10 p-3">
                      <div className="flex items-center justify-between">
                        <span className="font-bold">{t('netIncome')}</span>
                        <div className="flex items-center gap-1.5">
                          {(pnl.netIncome ?? 0) >= 0 ? <ArrowUpRight className="h-4 w-4 text-success-text" /> : <ArrowDownRight className="h-4 w-4 text-destructive-text" />}
                          <span className={`text-lg font-bold font-mono ${(pnl.netIncome ?? 0) >= 0 ? 'text-success-text' : 'text-destructive-text'}`}>
                            {formatCurrency(pnl.netIncome ?? 0)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <ResponsiveContainer width="100%" height={160}>
                      <BarChart data={[
                        { name: t('revenue'), value: pnl.revenue?.total ?? 0 },
                        { name: t('cogs'), value: pnl.costOfGoods?.total ?? 0 },
                        { name: t('expenses'), value: pnl.operatingExpenses?.total ?? 0 },
                        { name: t('netIncome'), value: pnl.netIncome ?? 0 },
                      ]} margin={{ top: 5, right: 10, bottom: 5, left: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                        <XAxis stroke={axis} dataKey="name" tick={{ fontSize: 10, fill: axis }} />
                        <YAxis stroke={axis} tick={{ fontSize: 10, fill: axis }} />
                        <Tooltip formatter={tooltipFmt} />
                        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                          {[{ v: pnl.revenue?.total ?? 0 }, { v: pnl.costOfGoods?.total ?? 0 }, { v: pnl.operatingExpenses?.total ?? 0 }, { v: pnl.netIncome ?? 0 }]
                            .map((e, i) => <Cell key={i} fill={i === 0 ? status.success : i === 3 ? (e.v >= 0 ? status.info : status.destructive) : status.warning} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
            {balanceSheet && (
              <Card className="animate-fade-in-up">
                <CardHeader><CardTitle className="text-lg flex items-center gap-2"><FileText className="h-5 w-5" /> {t('balanceSheetSummary')}</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div className="rounded-lg bg-info/10 p-3">
                        <p className="text-[11px] text-muted-foreground font-medium">{t('assets')}</p>
                        <p className="text-lg font-bold font-mono">{formatCurrency(balanceSheet.assets?.total ?? 0)}</p>
                      </div>
                      <div className="rounded-lg bg-warning/10 p-3">
                        <p className="text-[11px] text-muted-foreground font-medium">{t('liabilities')}</p>
                        <p className="text-lg font-bold font-mono">{formatCurrency(balanceSheet.liabilities?.total ?? 0)}</p>
                      </div>
                      <div className="rounded-lg bg-primary/10 p-3">
                        <p className="text-[11px] text-muted-foreground font-medium">{t('equity')}</p>
                        <p className="text-lg font-bold font-mono">{formatCurrency(balanceSheet.equity?.total ?? 0)}</p>
                      </div>
                    </div>
                    {balanceSheet.totals && (
                      <div className={`rounded-lg p-2.5 text-center text-sm font-medium ${balanceSheet.totals.isBalanced ? 'bg-success/10 text-success-text' : 'bg-destructive/10 text-destructive-text'}`}>
                        {balanceSheet.totals.isBalanced ? t('balanceSheetBalanced') : t('balanceSheetNotBalanced')}
                      </div>
                    )}
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie data={[
                          ...(balanceSheet.assets?.accounts || []).map((a: BalanceSheetAccountRow) => ({ name: a.accountName, value: Math.abs(a.balance) })),
                        ]} cx="50%" cy="50%" outerRadius={80} innerRadius={40} dataKey="value" paddingAngle={2}
                          label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`} labelLine={false}>
                          {(balanceSheet.assets?.accounts || []).map((_: BalanceSheetAccountRow, i: number) => <Cell key={i} fill={seriesAt(i)} />)}
                        </Pie>
                        <Tooltip formatter={tooltipFmt} />
                      </PieChart>
                    </ResponsiveContainer>
                    <p className="text-xs text-center text-muted-foreground">{t('assetsComposition')}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Tax Collection Summary */}
          {taxReport && (taxReport.byTaxRate?.length > 0 || taxReport.totals?.invoiceCount > 0) && (
            <Card className="animate-fade-in-up">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-lg flex items-center gap-2"><Receipt className="h-5 w-5 text-warning-text" /> {t('taxCollectionSummary')}</CardTitle>
                  {taxReport.byTaxRate?.length > 0 && (
                    <Button variant="outline" size="sm" onClick={() => downloadCSV('tax-summary.csv',
                      ['Tax Name', 'Rate %', 'Type', 'Amount Collected', 'Invoices'],
                      (taxReport.byTaxRate ?? []).map((r: TaxRateRow) => [r.taxName, r.taxRate, r.taxType, Number(r.totalAmount).toFixed(2), r.invoiceCount])
                    )}><Download className="me-1 h-3.5 w-3.5" />{t('csv')}</Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {[
                    { label: t('totalTaxCollected'), value: taxReport.totals?.totalTaxCollected, color: 'text-warning-text' },
                    { label: t('furtherTax'), value: taxReport.totals?.totalFurtherTax, color: 'text-warning-text' },
                    { label: t('totalRevenueInclTax'), value: taxReport.totals?.totalRevenue, color: 'text-success-text' },
                    { label: t('invoices'), value: taxReport.totals?.invoiceCount, color: '', isCount: true },
                  ].map(({ label, value, color, isCount }) => (
                    <div key={label} className="rounded-lg border bg-muted/30 p-3 text-center">
                      <p className="text-[11px] text-muted-foreground">{label}</p>
                      <p className={`mt-1 font-bold font-mono text-base ${color}`}>{isCount ? value : formatCurrency(value ?? 0)}</p>
                    </div>
                  ))}
                </div>
                {taxReport.byTaxRate?.length > 0 && (
                  <div className="overflow-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b text-muted-foreground">
                        <th className="pb-2 text-start font-medium">{t('taxName')}</th>
                        <th className="pb-2 text-end font-medium">{t('rate')}</th>
                        <th className="pb-2 text-end font-medium">{t('type')}</th>
                        <th className="pb-2 text-end font-medium">{t('amount')}</th>
                        <th className="pb-2 text-end font-medium">{t('invoices')}</th>
                      </tr></thead>
                      <tbody className="divide-y">
                        {taxReport.byTaxRate.map((r: TaxRateRow, i: number) => (
                          <tr key={i} className="hover:bg-accent/50">
                            <td className="py-2 font-medium">{r.taxName}</td>
                            <td className="py-2 text-end">{r.taxRate}%</td>
                            <td className="py-2 text-end text-xs"><span className={`rounded-full px-2 py-0.5 ${r.taxType === 'INCLUSIVE' ? 'bg-info/10 text-info-text' : 'bg-primary/10 text-primary-text'}`}>{humanizeEnum(r.taxType)}</span></td>
                            <td className="py-2 text-end font-mono text-warning-text">{formatCurrency(r.totalAmount)}</td>
                            <td className="py-2 text-end">{r.invoiceCount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Expense Trend */}
          {expenseTrend && expenseTrend.length > 0 && (
            <Card className="animate-fade-in-up">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-lg flex items-center gap-2"><Wallet className="h-5 w-5 text-destructive-text" /> {t('expenseTrendByCategory')}</CardTitle>
                  <Button variant="outline" size="sm" onClick={() => downloadCSV('expense-trend.csv',
                    ['Category', 'Amount', '% of Total'],
                    (() => {
                      const total = (expenseTrend ?? []).reduce((s: number, r) => s + Number(r.amount ?? 0), 0);
                      return (expenseTrend ?? []).map((r) => [r.categoryName, Number(r.amount ?? 0).toFixed(2), total > 0 ? (Number(r.amount ?? 0) / total * 100).toFixed(1) + '%' : '0%']);
                    })()
                  )}><Download className="me-1 h-3.5 w-3.5" />{t('csv')}</Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {expenseTrend[0]?.monthly ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={expenseTrend[0].monthly} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                      <XAxis stroke={axis} dataKey="month" tick={{ fontSize: 11, fill: axis }} />
                      <YAxis stroke={axis} tick={{ fontSize: 11, fill: axis }} />
                      <Tooltip formatter={tooltipFmt} />
                      <Legend />
                      {expenseTrend.map((cat, i: number) => (
                        <Line key={cat.categoryName} type="monotone" dataKey={cat.categoryName} stroke={seriesAt(i)} strokeWidth={2} dot={false} />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                ) : null}
                <div className="overflow-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b text-muted-foreground">
                      <th className="pb-2 text-start font-medium">{t('category')}</th>
                      <th className="pb-2 text-end font-medium">{t('amount')}</th>
                      <th className="pb-2 text-end font-medium">{t('percentOfTotal')}</th>
                    </tr></thead>
                    <tbody className="divide-y">
                      {(() => {
                        const total = (expenseTrend ?? []).reduce((s: number, r) => s + Number(r.amount ?? 0), 0);
                        return expenseTrend.map((r, i: number) => (
                          <tr key={r.categoryId ?? r.categoryName} className="hover:bg-accent/50">
                            <td className="py-2 font-medium flex items-center gap-2">
                              <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: seriesAt(i) }} />
                              {r.categoryName}
                            </td>
                            <td className="py-2 text-end font-mono text-destructive-text">{formatCurrency(r.amount)}</td>
                            <td className="py-2 text-end">
                              <span className="text-xs text-muted-foreground">
                                {total > 0 ? (Number(r.amount ?? 0) / total * 100).toFixed(1) : '0'}%
                              </span>
                            </td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                    <tfoot><tr className="border-t font-bold">
                      <td className="py-2">{t('total')}</td>
                      <td className="py-2 text-end font-mono text-destructive-text">
                        {formatCurrency((expenseTrend ?? []).reduce((s: number, r) => s + Number(r.amount ?? 0), 0))}
                      </td>
                      <td className="py-2 text-end text-xs text-muted-foreground">100%</td>
                    </tr></tfoot>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Inventory Valuation */}
          {canViewInventory && inventoryValuation && inventoryValuation.products?.length > 0 && (
            <Card className="animate-fade-in-up">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2"><Boxes className="h-5 w-5 text-info-text" /> {t('inventoryValuation')}</CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">{t('totalStockValue')}: <span className="font-mono font-semibold text-info-text">{formatCurrency(inventoryValuation.totalValue)}</span> ({inventoryValuation.productCount} {t('products')})</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => downloadCSV('inventory-valuation.csv',
                    ['Product', 'SKU', 'Category', 'Stock Qty', 'Cost Price', 'Stock Value'],
                    (inventoryValuation.products ?? []).map((p: ValuationProductRow) => [p.productName, p.sku || '', p.categoryName || '', p.stockQuantity, Number(p.costPrice).toFixed(2), Number(p.stockValue).toFixed(2)])
                  )}><Download className="me-1 h-3.5 w-3.5" />{t('csv')}</Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-auto max-h-72">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-background"><tr className="border-b text-muted-foreground">
                      <th className="pb-2 text-start font-medium">{t('product')}</th>
                      <th className="pb-2 text-start font-medium">{t('sku')}</th>
                      <th className="pb-2 text-start font-medium">{t('category')}</th>
                      <th className="pb-2 text-end font-medium">{t('stock')}</th>
                      <th className="pb-2 text-end font-medium">{t('costPerUnit')}</th>
                      <th className="pb-2 text-end font-medium">{t('totalValue')}</th>
                    </tr></thead>
                    <tbody className="divide-y">
                      {inventoryValuation.products.map((p: ValuationProductRow) => (
                        <tr key={`${p.productId}:${p.variantId ?? ""}`} className="hover:bg-accent/50">
                          <td className="py-2 font-medium">{p.productName}</td>
                          <td className="py-2 font-mono text-xs text-muted-foreground">{p.sku || '—'}</td>
                          <td className="py-2 text-xs text-muted-foreground">{p.categoryName || '—'}</td>
                          <td className="py-2 text-end">{p.stockQuantity}</td>
                          <td className="py-2 text-end font-mono">{formatCurrency(p.costPrice)}</td>
                          <td className="py-2 text-end font-mono font-semibold text-info-text">{formatCurrency(p.stockValue)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot><tr className="border-t font-bold">
                      <td colSpan={5} className="py-2 text-end">{t('totalStockValue')}</td>
                      <td className="py-2 text-end font-mono text-info-text">{formatCurrency(inventoryValuation.totalValue)}</td>
                    </tr></tfoot>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Payment Method Breakdown */}
          <Card className="animate-fade-in-up">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2"><CreditCard className="h-5 w-5 text-primary-text" /> Sales by Payment Method</CardTitle>
                  <p className="text-xs text-muted-foreground mt-0.5">Revenue breakdown by how customers paid</p>
                </div>
                {paymentMethodRows.length > 0 && (
                  <Button variant="outline" size="sm" onClick={() => downloadCSV('payment-methods.csv',
                    ['Method', 'Transactions', 'Total Amount', 'Avg Amount', '% of Revenue'],
                    paymentMethodRows.map(r => [r.method, r.transactionCount, r.totalAmount.toFixed(2), r.avgAmount.toFixed(2), r.percentageOfRevenue.toFixed(1) + '%'])
                  )}>
                    <Download className="h-4 w-4 me-1" /> CSV
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingPaymentMethods ? (
                <SkeletonTable rows={4} cols={5} />
              ) : paymentMethodRows.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No sales data for this period.</p>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {paymentMethodRows.map((r) => (
                      <div key={r.method} className="flex-1 min-w-[140px] rounded-lg border bg-muted/30 p-3 text-center">
                        <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{PAYMENT_LABELS[r.method] || r.method}</div>
                        <div className="text-lg font-bold">{formatCurrency(r.totalAmount)}</div>
                        <div className="text-xs text-muted-foreground">{r.percentageOfRevenue.toFixed(1)}% · {r.transactionCount} txns</div>
                      </div>
                    ))}
                  </div>
                  <div className="overflow-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b">
                        <th className="px-4 py-3 text-start text-xs font-semibold uppercase tracking-wide text-muted-foreground">Method</th>
                        <th className="px-4 py-3 text-end text-xs font-semibold uppercase tracking-wide text-muted-foreground">Transactions</th>
                        <th className="px-4 py-3 text-end text-xs font-semibold uppercase tracking-wide text-muted-foreground">Total Amount</th>
                        <th className="px-4 py-3 text-end text-xs font-semibold uppercase tracking-wide text-muted-foreground">Avg. Amount</th>
                        <th className="px-4 py-3 text-end text-xs font-semibold uppercase tracking-wide text-muted-foreground">% of Revenue</th>
                      </tr></thead>
                      <tbody className="divide-y">
                        {paymentMethodRows.map((r) => (
                          <tr key={r.method} className="hover:bg-muted/50">
                            <td className="px-4 py-3 font-medium">{PAYMENT_LABELS[r.method] || r.method}</td>
                            <td className="px-4 py-3 text-end">{r.transactionCount.toLocaleString()}</td>
                            <td className="px-4 py-3 text-end font-mono font-semibold">{formatCurrency(r.totalAmount)}</td>
                            <td className="px-4 py-3 text-end font-mono">{formatCurrency(r.avgAmount)}</td>
                            <td className="px-4 py-3 text-end">
                              <div className="flex items-center justify-end gap-2">
                                <div className="h-1.5 rounded-full bg-primary" style={{ width: `${Math.max(4, r.percentageOfRevenue)}px`, maxWidth: '80px' }} />
                                <span>{r.percentageOfRevenue.toFixed(1)}%</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ══════════════ TAB: PURCHASES ══════════════ */}
      {activeTab === 'purchases' && canViewPurchases && (
        <div className="space-y-6">
          {purchaseReport && purchaseReport.bySupplier?.length > 0 ? (
            <Card className="animate-fade-in-up">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2"><Truck className="h-5 w-5 text-destructive-text" /> {t('purchaseSummaryBySupplier')}</CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">{t('totalOrderedAcross', { amount: formatCurrency(purchaseReport.totals?.totalOrderedValue), count: purchaseReport.totals?.totalOrders ?? 0 })}</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => downloadCSV('purchase-summary.csv',
                    ['Supplier', 'Orders', 'Received', 'Pending', 'Cancelled', 'Total Ordered', 'Total Received'],
                    (purchaseReport.bySupplier ?? []).map((r: PurchaseSupplierRow) => [r.supplierName, r.orderCount, r.receivedCount, r.pendingCount, r.cancelledCount, Number(r.totalOrdered).toFixed(2), Number(r.totalReceived).toFixed(2)])
                  )}><Download className="me-1 h-3.5 w-3.5" />{t('csv')}</Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b text-muted-foreground">
                      <th className="pb-2 text-start font-medium">{t('supplier')}</th>
                      <th className="pb-2 text-end font-medium">{t('orders')}</th>
                      <th className="pb-2 text-end font-medium">{t('received')}</th>
                      <th className="pb-2 text-end font-medium">{t('pending')}</th>
                      <th className="pb-2 text-end font-medium">{t('totalOrdered')}</th>
                      <th className="pb-2 text-end font-medium">{t('totalReceived')}</th>
                    </tr></thead>
                    <tbody className="divide-y">
                      {purchaseReport.bySupplier.map((r: PurchaseSupplierRow) => (
                        <tr key={r.supplierId} className="hover:bg-accent/50">
                          <td className="py-2 font-medium">{r.supplierName}</td>
                          <td className="py-2 text-end">{r.orderCount}</td>
                          <td className="py-2 text-end text-success-text">{r.receivedCount}</td>
                          <td className="py-2 text-end text-warning-text">{r.pendingCount}</td>
                          <td className="py-2 text-end font-mono">{formatCurrency(r.totalOrdered)}</td>
                          <td className="py-2 text-end font-mono text-success-text">{formatCurrency(r.totalReceived)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">{t('noPurchaseData')}</CardContent></Card>
          )}
        </div>
      )}

      {/* ══════════════ TAB: LOCATIONS ══════════════ */}
      {activeTab === 'locations' && canViewLocations && (
        <div className="space-y-6">
          {locationReport && locationReport.length > 1 ? (
            <Card className="animate-fade-in-up">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-lg flex items-center gap-2"><Building2 className="h-5 w-5 text-primary-text" /> {t('locationComparison')}</CardTitle>
                  <Button variant="outline" size="sm" onClick={() => downloadCSV('location-comparison.csv',
                    ['Location', 'Sales Count', 'Revenue', 'Avg Basket', 'Tax Collected', 'Voids'],
                    (locationReport ?? []).map((r) => [r.locationName, r.salesCount, Number(r.revenue).toFixed(2), Number(r.avgBasket).toFixed(2), Number(r.taxCollected).toFixed(2), r.voidedCount])
                  )}><Download className="me-1 h-3.5 w-3.5" />{t('csv')}</Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={locationReport} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                    <XAxis stroke={axis} dataKey="locationName" tick={{ fontSize: 11, fill: axis }} />
                    <YAxis stroke={axis} tick={{ fontSize: 11, fill: axis }} />
                    <Tooltip formatter={tooltipFmt} />
                    <Legend />
                    <Bar dataKey="revenue" name={t('revenue')} fill={series[0]} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="taxCollected" name={t('tax')} fill={series[1]} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                <div className="overflow-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b text-muted-foreground">
                      <th className="pb-2 text-start font-medium">{t('location')}</th>
                      <th className="pb-2 text-end font-medium">{t('salesCount')}</th>
                      <th className="pb-2 text-end font-medium">{t('revenue')}</th>
                      <th className="pb-2 text-end font-medium">{t('avgBasket')}</th>
                      <th className="pb-2 text-end font-medium">{t('tax')}</th>
                      <th className="pb-2 text-end font-medium">{t('voids')}</th>
                    </tr></thead>
                    <tbody className="divide-y">
                      {locationReport.map((r) => (
                        <tr key={r.locationId} className="hover:bg-accent/50">
                          <td className="py-2 font-medium flex items-center gap-1.5"><MapPin className="h-3 w-3 text-primary-text" />{r.locationName}</td>
                          <td className="py-2 text-end">{r.salesCount}</td>
                          <td className="py-2 text-end font-mono">{formatCurrency(r.revenue)}</td>
                          <td className="py-2 text-end font-mono">{formatCurrency(r.avgBasket)}</td>
                          <td className="py-2 text-end font-mono text-warning-text">{formatCurrency(r.taxCollected)}</td>
                          <td className="py-2 text-end text-destructive-text">{r.voidedCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-10 text-center text-sm text-muted-foreground">
                {locationReport && locationReport.length === 1
                  ? t('onlyOneLocationData')
                  : t('noLocationData')}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* ══════════════ TAB: INVENTORY ══════════════ */}
      {activeTab === 'inventory' && canViewInventory && (
        <div className="space-y-6">

          {/* Stock Valuation */}
          <Card className="animate-fade-in-up">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2"><Boxes className="h-5 w-5 text-info-text" /> {t('stockValuationTitle')}</CardTitle>
                  <p className="text-xs text-muted-foreground mt-0.5">{t('stockValuationSubtitle')}</p>
                </div>
                {inventoryValuation?.products?.length > 0 && (
                  <Button variant="outline" size="sm" onClick={() => downloadCSV('stock-valuation.csv',
                    ['Product', 'SKU', 'Category', 'Stock Qty', 'Cost Price', 'Sale Price', 'Total Cost Value', 'Total Sale Value'],
                    [...(inventoryValuation.products ?? [])].sort((a: ValuationProductRow, b: ValuationProductRow) => Number(b.stockValue ?? 0) - Number(a.stockValue ?? 0))
                      .map((p: ValuationProductRow) => [p.productName, p.sku || '', p.categoryName || '', p.stockQuantity, Number(p.costPrice).toFixed(2), Number(p.salePrice).toFixed(2), Number(p.stockValue).toFixed(2), Number(p.saleValue).toFixed(2)])
                  )}><Download className="me-1 h-3.5 w-3.5" />{t('csv')}</Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {isLoadingInventoryValuation ? (
                <SkeletonTable rows={8} cols={8} />
              ) : !inventoryValuation?.products?.length ? (
                <EmptyState icon={Boxes} title={t('noStockValuationData')} subtitle={t('addProductsForValuation')} />
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="rounded-lg border bg-info/10 p-4 text-center">
                      <p className="text-[11px] text-muted-foreground font-medium">{t('totalCostValue')}</p>
                      <p className="mt-1 text-xl font-bold font-mono text-info-text">{formatCurrency(inventoryValuation.totalValue ?? 0)}</p>
                    </div>
                    <div className="rounded-lg border bg-success/10 p-4 text-center">
                      <p className="text-[11px] text-muted-foreground font-medium">{t('totalSaleValue')}</p>
                      <p className="mt-1 text-xl font-bold font-mono text-success-text">{formatCurrency(inventoryValuation.totalSaleValue ?? 0)}</p>
                    </div>
                    <div className="rounded-lg border bg-primary/10 p-4 text-center">
                      <p className="text-[11px] text-muted-foreground font-medium">{t('potentialProfit')}</p>
                      <p className="mt-1 text-xl font-bold font-mono text-primary-text">{formatCurrency((inventoryValuation.totalSaleValue ?? 0) - (inventoryValuation.totalValue ?? 0))}</p>
                    </div>
                  </div>
                  <div className="overflow-auto max-h-96">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-background">
                        <tr className="border-b text-muted-foreground">
                          <th className="pb-2 text-start font-medium">{t('product')}</th>
                          <th className="pb-2 text-start font-medium">{t('sku')}</th>
                          <th className="pb-2 text-start font-medium">{t('category')}</th>
                          <th className="pb-2 text-end font-medium">{t('stockQty')}</th>
                          <th className="pb-2 text-end font-medium">{t('costPrice')}</th>
                          <th className="pb-2 text-end font-medium">{t('salePrice')}</th>
                          <th className="pb-2 text-end font-medium">{t('costValue')}</th>
                          <th className="pb-2 text-end font-medium">{t('saleValue')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {[...(inventoryValuation.products ?? [])].sort((a: ValuationProductRow, b: ValuationProductRow) => Number(b.stockValue ?? 0) - Number(a.stockValue ?? 0)).map((p: ValuationProductRow) => (
                          <tr key={`${p.productId}:${p.variantId ?? ""}`} className="hover:bg-accent/50">
                            <td className="py-2 font-medium">{p.productName}</td>
                            <td className="py-2 font-mono text-xs text-muted-foreground">{p.sku || '—'}</td>
                            <td className="py-2 text-xs text-muted-foreground">{p.categoryName || '—'}</td>
                            <td className="py-2 text-end">{p.stockQuantity}</td>
                            <td className="py-2 text-end font-mono">{formatCurrency(p.costPrice)}</td>
                            <td className="py-2 text-end font-mono">{formatCurrency(p.salePrice)}</td>
                            <td className="py-2 text-end font-mono font-semibold text-info-text">{formatCurrency(p.stockValue)}</td>
                            <td className="py-2 text-end font-mono font-semibold text-success-text">{formatCurrency(p.saleValue)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* POS-I246: stock valuation vs Inventory account 1200 */}
          <StockLedgerCheckCard />

          {/* Inventory Turnover */}
          <Card className="animate-fade-in-up">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2"><RefreshCw className="h-5 w-5 text-primary-text" /> {t('inventoryTurnover')}</CardTitle>
                  <p className="text-xs text-muted-foreground mt-0.5">{t('turnoverPeriodNote')} <span className="text-success-text font-medium">{t('turnoverGoodLabel')}</span> · <span className="text-warning-text font-medium">{t('turnoverAvgLabel')}</span> · <span className="text-destructive-text font-medium">{t('turnoverSlowLabel')}</span></p>
                </div>
                {turnoverProducts.length > 0 && (
                  <Button variant="outline" size="sm" onClick={() => downloadCSV('inventory-turnover.csv',
                    ['Product', 'SKU', 'Units Sold', 'Units Received', 'Current Stock', 'Cost Value', 'Turnover Ratio'],
                    turnoverProducts.map((r: TurnoverRow) => [r.productName, r.sku || '', r.unitsSold ?? 0, r.unitsReceived ?? 0, r.currentStock ?? 0, Number(r.costValue ?? 0).toFixed(2), Number(r.turnoverRatio ?? 0).toFixed(2)])
                  )}><Download className="me-1 h-3.5 w-3.5" />{t('csv')}</Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingTurnover ? (
                <SkeletonTable rows={8} cols={7} />
              ) : !turnoverProducts.length ? (
                <EmptyState icon={RefreshCw} title={t('noTurnoverData')} subtitle={t('turnoverDataNeeded')} />
              ) : (
                <div className="overflow-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-muted-foreground">
                        <th className="pb-2 text-start font-medium">{t('product')}</th>
                        <th className="pb-2 text-start font-medium">{t('sku')}</th>
                        <th className="pb-2 text-end font-medium">{t('unitsSold')}</th>
                        <th className="pb-2 text-end font-medium">{t('unitsReceived')}</th>
                        <th className="pb-2 text-end font-medium">{t('currentStock')}</th>
                        <th className="pb-2 text-end font-medium">{t('costValue')}</th>
                        <th className="pb-2 text-end font-medium">{t('turnoverRatio')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {turnoverProducts.map((r: TurnoverRow) => {
                        const ratio = Number(r.turnoverRatio ?? 0);
                        const ratioColor = ratio > 2
                          ? 'bg-success/10 text-success-text'
                          : ratio >= 0.5
                          ? 'bg-warning/10 text-warning-text'
                          : 'bg-destructive/10 text-destructive-text';
                        return (
                          <tr key={r.productId ?? r.sku} className="hover:bg-accent/50">
                            <td className="py-2 font-medium">{r.productName}</td>
                            <td className="py-2 font-mono text-xs text-muted-foreground">{r.sku || '—'}</td>
                            <td className="py-2 text-end">{r.unitsSold ?? 0}</td>
                            <td className="py-2 text-end">{r.unitsReceived ?? 0}</td>
                            <td className="py-2 text-end">{r.currentStock ?? 0}</td>
                            <td className="py-2 text-end font-mono">{formatCurrency(r.costValue)}</td>
                            <td className="py-2 text-end">
                              <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold ${ratioColor}`}>
                                {ratio.toFixed(2)}x
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

        </div>
      )}

      {/* ══════════════ TAB: FBR ══════════════ */}
      {activeTab === 'fbr' && canViewFbr && (
        <div className="space-y-6">
          <Card className="animate-fade-in-up">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><Receipt className="h-5 w-5 text-info-text" /> {t('fbrSubmissionSummary')}</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">{t('fbrDateRangeNote')}</p>
            </CardHeader>
            <CardContent className="space-y-6">
              {isLoadingFbr ? (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="h-24 rounded-lg bg-muted animate-pulse" />
                  ))}
                </div>
              ) : !fbrSummary ? (
                <EmptyState icon={Receipt} title={t('noFbrData')} subtitle={t('fbrDataHint')} />
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {([
                      { label: t('totalSubmitted'), value: fbrSummary.totalSubmitted ?? 0, icon: Receipt, accent: 'info' },
                      { label: t('accepted'), value: fbrSummary.accepted ?? 0, icon: CheckCircle, accent: 'success' },
                      { label: t('rejected'), value: fbrSummary.rejected ?? 0, icon: XCircle, accent: 'danger' },
                      { label: t('failed'), value: fbrSummary.failed ?? 0, icon: AlertCircle, accent: 'danger' },
                      { label: t('fbrOffline'), value: fbrSummary.offline ?? 0, icon: WifiOff, accent: 'default' },
                    ] as { label: string; value: number; icon: ComponentType<{ className?: string }>; accent: StatCardProps['accent'] }[]).map(({ label, value, icon, accent }) => (
                      <StatCard key={label} label={label} value={value} format="raw" icon={icon} accent={accent} />
                    ))}
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="rounded-lg border p-5 text-center">
                      <p className="text-sm font-medium text-muted-foreground mb-3">{t('successRate')}</p>
                      {(() => {
                        const rate = Number(fbrSummary.successRate ?? 0);
                        const color = rate >= 95 ? 'text-success-text' : rate >= 80 ? 'text-warning-text' : 'text-destructive-text';
                        return (
                          <>
                            <p className={`text-5xl font-bold font-mono ${color}`}>{rate.toFixed(1)}%</p>
                            <p className="text-xs text-muted-foreground mt-2">{t('submissionsAcceptedByFbr')}</p>
                          </>
                        );
                      })()}
                    </div>
                    <div className="rounded-lg border p-5 text-center">
                      <Clock className="h-5 w-5 text-muted-foreground mx-auto mb-3" />
                      <p className="text-sm font-medium text-muted-foreground mb-2">{t('avgProcessingTime')}</p>
                      <p className="text-5xl font-bold font-mono text-foreground">{fbrSummary.avgProcessingTimeMs != null ? Number(fbrSummary.avgProcessingTimeMs).toLocaleString() : '—'}</p>
                      <p className="text-xs text-muted-foreground mt-2">{t('msPerSubmission')}</p>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ═══════════════════ INVOICE DETAIL MODAL ═══════════════════ */}
      <Dialog
        visible={!!invoiceId}
        onHide={() => setInvoiceId(null)}
        header={`${t('invoiceHeader')} ${invoiceDetail?.invoiceNumber || '…'}`}
        footer={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={printInvoice}><Printer className="me-1 h-3.5 w-3.5" />{t('print')}</Button>
            <Button variant="outline" size="sm" onClick={() => setInvoiceId(null)}>{t('close')}</Button>
          </div>
        }
        style={{ width: '42rem' }}
        draggable={false}
        resizable={false}
      >
        {!invoiceDetail ? (
          <div className="p-8 text-center text-muted-foreground">{t('loading')}</div>
        ) : (
          <div className="space-y-5">
            {/* Meta */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs text-muted-foreground font-medium">{t('invoiceNumber')}</p>
                <p className="font-mono font-medium">{invoiceDetail.invoiceNumber}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-medium">{t('dateAndTime')}</p>
                <p>{new Date(invoiceDetail.createdAt).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-medium">{t('customer')}</p>
                <p>{invoiceDetail.customer?.name || t('walkInCustomer')}</p>
                {invoiceDetail.customer?.phone && <p className="text-xs text-muted-foreground">{invoiceDetail.customer.phone}</p>}
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-medium">{t('status')}</p>
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${invoiceDetail.status === 'COMPLETED' ? 'bg-success/10 text-success-text' : 'bg-destructive/10 text-destructive-text'}`}>
                  {invoiceDetail.status}
                </span>
              </div>
            </div>

            {/* Items table */}
            <div className="overflow-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30 text-muted-foreground">
                    <th className="px-3 py-2 text-start font-medium">{t('product')}</th>
                    <th className="px-3 py-2 text-start font-medium">{t('sku')}</th>
                    <th className="px-3 py-2 text-end font-medium">{t('qty')}</th>
                    <th className="px-3 py-2 text-end font-medium">{t('unitPrice')}</th>
                    <th className="px-3 py-2 text-end font-medium">{t('tax')}</th>
                    <th className="px-3 py-2 text-end font-medium">{t('total')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {(invoiceDetail.items || []).map((item: InvoiceItemRow) => (
                    <tr key={item.id} className="hover:bg-accent/30 transition-colors">
                      <td className="px-3 py-2">
                        <p className="font-medium">{item.productName}</p>
                        {Number(item.discountAmount) > 0 && (
                          <p className="text-[11px] text-success-text">
                            {t('discount')} -{formatCurrency(Number(item.discountAmount))}
                            {item.discountType === 'PERCENTAGE' ? ` (${Number(item.discountValue)}%)` : ''}
                            {item.discountReason ? ` · ${item.discountReason}` : ''}
                          </p>
                        )}
                        {item.taxBreakdown?.length > 0 && (
                          <div className="flex gap-1 mt-0.5">
                            {item.taxBreakdown.map((tb: InvoiceTaxBreakdownRow, i: number) => (
                              <span key={i} className="text-[10px] text-muted-foreground bg-muted rounded px-1">{tb.name.includes('%') ? tb.name : `${tb.name} ${tb.rate}%`}</span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{item.productSku || '—'}</td>
                      <td className="px-3 py-2 text-end">{item.quantity}</td>
                      <td className="px-3 py-2 text-end font-mono">{formatCurrency(item.unitPrice)}</td>
                      <td className="px-3 py-2 text-end font-mono text-xs">{formatCurrency(item.taxAmount)}</td>
                      <td className="px-3 py-2 text-end font-mono font-medium">{formatCurrency(item.totalAmount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">{t('subtotal')}</span><span className="font-mono">{formatCurrency(Number(invoiceDetail.subtotal) + Number(invoiceDetail.lineDiscountAmount || 0))}</span></div>
              {Number(invoiceDetail.lineDiscountAmount) > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('itemDiscounts')}</span>
                  <span className="font-mono text-destructive-text">-{formatCurrency(invoiceDetail.lineDiscountAmount)}</span>
                </div>
              )}
              {Number(invoiceDetail.discountAmount) > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('discount')} {invoiceDetail.discountType === 'PERCENTAGE' ? `(${invoiceDetail.discountValue}%)` : ''}</span>
                  <span className="font-mono text-destructive-text">-{formatCurrency(invoiceDetail.discountAmount)}</span>
                </div>
              )}
              <div className="flex justify-between"><span className="text-muted-foreground">{t('tax')}</span><span className="font-mono">{formatCurrency(invoiceDetail.taxAmount)}</span></div>
              <div className="flex justify-between border-t pt-2 text-base font-bold">
                <span>{t('total')}</span><span className="font-mono">{formatCurrency(invoiceDetail.totalAmount)}</span>
              </div>
            </div>

            {/* Payment info */}
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1.5">{t('payments')}</p>
              <div className="flex gap-2 flex-wrap">
                {(invoiceDetail.payments || []).map((p: InvoicePaymentRow, i: number) => (
                  <div key={i} className="rounded-lg border px-3 py-2 text-sm">
                    <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
                      <span className="h-2 w-2 rounded-full" style={{ background: paymentColor(p.method) }} aria-hidden="true" />
                      {PAYMENT_LABELS[p.method] || p.method}
                    </span>
                    <span className="ms-2 font-mono">{formatCurrency(p.amount)}</span>
                    {p.reference && <span className="ms-2 text-xs text-muted-foreground">{t('ref')}: {p.reference}</span>}
                  </div>
                ))}
              </div>
              {Number(invoiceDetail.changeAmount) > 0 && (
                <p className="mt-1.5 text-xs text-muted-foreground">{t('changeReturned')}: <span className="font-mono font-medium">{formatCurrency(invoiceDetail.changeAmount)}</span></p>
              )}
            </div>

            {invoiceDetail.notes && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">{t('notes')}</p>
                <p className="text-sm text-muted-foreground">{invoiceDetail.notes}</p>
              </div>
            )}
          </div>
        )}
      </Dialog>

      {/* ══════════════ TAB: ABC ANALYSIS ══════════════ */}
      {activeTab === 'abc' && (
        <div className="space-y-4">
          {/* Summary stats */}
          {abcRows.length > 0 && (
            <div className="grid grid-cols-3 gap-3">
              {(['A', 'B', 'C'] as const).map((cls) => {
                const count = abcRows.filter(r => r.abcClass === cls).length;
                const colors = { A: 'bg-success/10 text-success-text', B: 'bg-warning/10 text-warning-text', C: 'bg-destructive/10 text-destructive-text' };
                const labels = { A: t('classALabel'), B: t('classBLabel'), C: t('classCLabel') };
                return (
                  <Card key={cls}>
                    <CardContent className="p-4 text-center">
                      <span className={`inline-block rounded-full px-3 py-1 text-lg font-bold ${colors[cls]}`}>{cls}</span>
                      <p className="mt-2 text-2xl font-bold">{count}</p>
                      <p className="text-xs text-muted-foreground">{labels[cls]}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-lg flex items-center gap-2"><LayoutGrid className="h-5 w-5" /> {t('abcInventoryAnalysis')}</CardTitle>
                {abcRows.length > 0 && (
                  <Button variant="outline" size="sm" onClick={() => downloadCSV('abc-analysis.csv',
                    ['Product', 'Revenue', 'Revenue Share %', 'Units Sold', 'Class'],
                    abcRows.map((r) => [r.productName, Number(r.revenue).toFixed(2), r.revenueShare, r.unitsSold, r.abcClass])
                  )}><Download className="me-1 h-3.5 w-3.5" />{t('csv')}</Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingAbc ? (
                <SkeletonTable rows={8} cols={5} />
              ) : abcRows.length === 0 ? (
                <EmptyState icon={Boxes} title={t('noData')} description={t('noAbcData')} />
              ) : (
                <div className="overflow-x-auto rounded-lg border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-4 py-2.5 text-start font-medium">{t('product')}</th>
                        <th className="px-4 py-2.5 text-end font-medium">{t('revenue')}</th>
                        <th className="px-4 py-2.5 text-end font-medium">{t('revenuePercent')}</th>
                        <th className="px-4 py-2.5 text-end font-medium">{t('unitsSold')}</th>
                        <th className="px-4 py-2.5 text-center font-medium">{t('class')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {abcRows.map((r) => (
                        <tr key={r.productId} className="border-b hover:bg-accent/30 transition-colors">
                          <td className="px-4 py-2.5 font-medium">{r.productName}</td>
                          <td className="px-4 py-2.5 text-end font-mono">{formatCurrency(r.revenue)}</td>
                          <td className="px-4 py-2.5 text-end text-muted-foreground">{r.revenueShare}%</td>
                          <td className="px-4 py-2.5 text-end">{r.unitsSold}</td>
                          <td className="px-4 py-2.5 text-center">
                            <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-bold ${
                              r.abcClass === 'A' ? 'bg-success/10 text-success-text' :
                              r.abcClass === 'B' ? 'bg-warning/10 text-warning-text' :
                              'bg-destructive/10 text-destructive-text'
                            }`}>{r.abcClass}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ══════════════ TAB: PROFIT MARGINS ══════════════ */}
      {activeTab === 'margins' && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <CardTitle className="text-lg flex items-center gap-2"><TrendingUp className="h-5 w-5" /> {t('productProfitMargins')}</CardTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  {marginsRows.length > 0 && (
                    <Button variant="outline" size="sm" onClick={() => downloadCSV('product-margins.csv',
                      ['Product', 'Units Sold', 'Revenue', 'COGS', 'Gross Profit', 'Margin %'],
                      marginsRows.map((r) => [r.productName, r.unitsSold, Number(r.revenue).toFixed(2), Number(r.cogs).toFixed(2), Number(r.grossProfit).toFixed(2), r.marginPct])
                    )}><Download className="me-1 h-3.5 w-3.5" />{t('csv')}</Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingMargins ? (
                <SkeletonTable rows={8} cols={6} />
              ) : marginsRows.length === 0 ? (
                <EmptyState icon={TrendingUp} title={t('noData')} description={t('noCompletedSales')} />
              ) : (
                <div className="overflow-x-auto rounded-lg border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-4 py-2.5 text-start font-medium">{t('product')}</th>
                        <th className="px-4 py-2.5 text-end font-medium">{t('unitsSold')}</th>
                        <th className="px-4 py-2.5 text-end font-medium">{t('revenue')}</th>
                        <th className="px-4 py-2.5 text-end font-medium">{t('cogs')}</th>
                        <th className="px-4 py-2.5 text-end font-medium">{t('grossProfit')}</th>
                        <th className="px-4 py-2.5 text-end font-medium">{t('marginPct')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {marginsRows.map((r) => (
                        <tr key={r.productId} className="border-b hover:bg-accent/30 transition-colors">
                          <td className="px-4 py-2.5 font-medium">{r.productName}</td>
                          <td className="px-4 py-2.5 text-end">{r.unitsSold}</td>
                          <td className="px-4 py-2.5 text-end font-mono">{formatCurrency(r.revenue)}</td>
                          <td className="px-4 py-2.5 text-end font-mono text-muted-foreground">{formatCurrency(r.cogs)}</td>
                          <td className={`px-4 py-2.5 text-end font-mono font-medium ${r.grossProfit >= 0 ? 'text-success-text' : 'text-destructive-text'}`}>
                            {formatCurrency(r.grossProfit)}
                          </td>
                          <td className="px-4 py-2.5 text-end">
                            <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                              Number(r.marginPct) >= 30 ? 'bg-success/10 text-success-text' :
                              Number(r.marginPct) >= 10 ? 'bg-warning/10 text-warning-text' :
                              'bg-destructive/10 text-destructive-text'
                            }`}>{r.marginPct}%</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ══════════════ TAB: SUPPLIER AGING ══════════════ */}
      {activeTab === 'supplier-aging' && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-lg flex items-center gap-2"><Truck className="h-5 w-5" /> {t('supplierAgingReport')}</CardTitle>
                {agingRows.length > 0 && (
                  <Button variant="outline" size="sm" onClick={() => downloadCSV('supplier-aging.csv',
                    ['Supplier', 'Current', '1-30 Days', '31-60 Days', '61-90 Days', 'Over 90 Days', 'Total Outstanding'],
                    agingRows.map((r) => [r.supplierName, Number(r.current).toFixed(2), Number(r.days30).toFixed(2), Number(r.days60).toFixed(2), Number(r.days90).toFixed(2), Number(r.over90).toFixed(2), Number(r.total).toFixed(2)])
                  )}><Download className="me-1 h-3.5 w-3.5" />{t('csv')}</Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingAging ? (
                <SkeletonTable rows={5} cols={7} />
              ) : agingRows.length === 0 ? (
                <EmptyState icon={Truck} title={t('noOutstandingPayables')} description={t('allPOsPaid')} />
              ) : (
                <div className="overflow-x-auto rounded-lg border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-4 py-2.5 text-start font-medium">{t('supplier')}</th>
                        <th className="px-4 py-2.5 text-end font-medium">{t('current')}</th>
                        <th className="px-4 py-2.5 text-end font-medium">{t('days1to30')}</th>
                        <th className="px-4 py-2.5 text-end font-medium">{t('days31to60')}</th>
                        <th className="px-4 py-2.5 text-end font-medium">{t('days61to90')}</th>
                        <th className="px-4 py-2.5 text-end font-medium">{t('daysOver90')}</th>
                        <th className="px-4 py-2.5 text-end font-medium">{t('totalOutstanding')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {agingRows.map((r) => (
                        <tr key={r.supplierId} className="border-b hover:bg-accent/30 transition-colors">
                          <td className="px-4 py-2.5 font-medium">{r.supplierName}</td>
                          <td className="px-4 py-2.5 text-end font-mono">{r.current > 0 ? formatCurrency(r.current) : '—'}</td>
                          <td className="px-4 py-2.5 text-end font-mono text-warning-text">{r.days30 > 0 ? formatCurrency(r.days30) : '—'}</td>
                          <td className="px-4 py-2.5 text-end font-mono text-warning-text">{r.days60 > 0 ? formatCurrency(r.days60) : '—'}</td>
                          <td className="px-4 py-2.5 text-end font-mono text-destructive-text">{r.days90 > 0 ? formatCurrency(r.days90) : '—'}</td>
                          <td className="px-4 py-2.5 text-end font-mono text-destructive-text font-semibold">{r.over90 > 0 ? formatCurrency(r.over90) : '—'}</td>
                          <td className="px-4 py-2.5 text-end font-mono font-bold">{formatCurrency(r.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t bg-muted/30">
                        <td className="px-4 py-2.5 font-semibold">{t('total')}</td>
                        <td className="px-4 py-2.5 text-end font-mono font-semibold">{formatCurrency(agingRows.reduce((s, r) => s + r.current, 0))}</td>
                        <td className="px-4 py-2.5 text-end font-mono font-semibold text-warning-text">{formatCurrency(agingRows.reduce((s, r) => s + r.days30, 0))}</td>
                        <td className="px-4 py-2.5 text-end font-mono font-semibold text-warning-text">{formatCurrency(agingRows.reduce((s, r) => s + r.days60, 0))}</td>
                        <td className="px-4 py-2.5 text-end font-mono font-semibold text-destructive-text">{formatCurrency(agingRows.reduce((s, r) => s + r.days90, 0))}</td>
                        <td className="px-4 py-2.5 text-end font-mono font-semibold text-destructive-text">{formatCurrency(agingRows.reduce((s, r) => s + r.over90, 0))}</td>
                        <td className="px-4 py-2.5 text-end font-mono font-bold">{formatCurrency(agingRows.reduce((s, r) => s + r.total, 0))}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ══════════════ TAB: COMMISSIONS ══════════════ */}
      {activeTab === 'commissions' && (
        <div className="space-y-4">
          {/* KPI Summary */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="border-primary/30 bg-primary/10">
              <CardContent className="p-5">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Total Commission</p>
                <p className="text-2xl font-bold text-primary-text mt-1">{formatCurrency(commissionReport?.totals?.commissionEarned ?? 0)}</p>
              </CardContent>
            </Card>
            <Card className="border-success/30 bg-success/10">
              <CardContent className="p-5">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Total Revenue</p>
                <p className="text-2xl font-bold text-success-text mt-1">{formatCurrency(commissionReport?.totals?.totalRevenue ?? 0)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Total Sales</p>
                <p className="text-2xl font-bold mt-1">{commissionReport?.totals?.salesCount ?? 0}</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <UserCheck className="h-5 w-5" /> Staff Commission Report
                </CardTitle>
                {(commissionReport?.rows ?? []).length > 0 && (
                  <Button variant="outline" size="sm" onClick={() => downloadCSV('commissions.csv',
                    ['Staff', 'Email', 'Commission Rate', 'Sales Count', 'Total Revenue', 'Commission Earned', 'Avg Basket'],
                    (commissionReport?.rows ?? []).map((r: CommissionRow) => [r.staffName, r.email, `${r.commissionRate}%`, r.salesCount, Number(r.totalRevenue).toFixed(2), Number(r.commissionEarned).toFixed(2), Number(r.avgBasket).toFixed(2)])
                  )}><Download className="me-1 h-3.5 w-3.5" /> CSV</Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingCommissions ? (
                <SkeletonTable rows={5} cols={6} />
              ) : (commissionReport?.rows ?? []).length === 0 ? (
                <EmptyState icon={UserCheck} title="No commission data" subtitle="No completed sales with commission found for the selected period. Assign commission rates to staff in the Users section." />
              ) : (
                <div className="overflow-x-auto rounded-lg border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-4 py-2.5 text-start font-medium">Staff Member</th>
                        <th className="px-4 py-2.5 text-start font-medium">Email</th>
                        <th className="px-4 py-2.5 text-end font-medium">Rate</th>
                        <th className="px-4 py-2.5 text-end font-medium">Sales</th>
                        <th className="px-4 py-2.5 text-end font-medium">Revenue</th>
                        <th className="px-4 py-2.5 text-end font-medium">Commission</th>
                        <th className="px-4 py-2.5 text-end font-medium">Avg Basket</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(commissionReport?.rows ?? []).map((r: CommissionRow) => (
                        <tr key={r.userId} className="border-b hover:bg-accent/30 transition-colors">
                          <td className="px-4 py-2.5 font-medium">{r.staffName}</td>
                          <td className="px-4 py-2.5 text-muted-foreground text-xs">{r.email}</td>
                          <td className="px-4 py-2.5 text-end">
                            {r.commissionRate > 0 ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary-text text-xs font-semibold">
                                {r.commissionRate}%
                              </span>
                            ) : '—'}
                          </td>
                          <td className="px-4 py-2.5 text-end font-mono">{r.salesCount}</td>
                          <td className="px-4 py-2.5 text-end font-mono">{formatCurrency(r.totalRevenue)}</td>
                          <td className="px-4 py-2.5 text-end font-mono font-semibold text-primary-text">{formatCurrency(r.commissionEarned)}</td>
                          <td className="px-4 py-2.5 text-end font-mono text-muted-foreground">{formatCurrency(r.avgBasket)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t bg-muted/30 font-semibold">
                        <td className="px-4 py-2.5" colSpan={3}>Total</td>
                        <td className="px-4 py-2.5 text-end font-mono">{commissionReport?.totals?.salesCount ?? 0}</td>
                        <td className="px-4 py-2.5 text-end font-mono">{formatCurrency(commissionReport?.totals?.totalRevenue ?? 0)}</td>
                        <td className="px-4 py-2.5 text-end font-mono text-primary-text">{formatCurrency(commissionReport?.totals?.commissionEarned ?? 0)}</td>
                        <td className="px-4 py-2.5 text-end"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ══════════════ TAB: RECOVERY & BI (MEI-009/010/011) ══════════════ */}
      {activeTab === 'recovery-bi' && canViewRecoveryBi && (
        <div className="space-y-6">
          <p className="text-sm text-muted-foreground">{t('recoveryIntro')}</p>
          {isRecoveryKpisError && (
            <AlertBanner tone="danger" icon={AlertCircle}>{t('recoveryLoadFailed')}</AlertBanner>
          )}
          {/* MEI-011 KPI tiles */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-success/30 bg-success/10">
              <CardContent className="p-5">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Recovery Rate</p>
                <p className="text-2xl font-bold text-success-text mt-1">
                  {isLoadingRecoveryKpis ? '…' : `${recoveryKpis?.recoveryRatePct ?? 0}%`}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {formatCurrency(recoveryKpis?.totalCollected ?? 0)} / {formatCurrency(recoveryKpis?.totalContracted ?? 0)}
                </p>
              </CardContent>
            </Card>
            <Card className="border-warning/30 bg-warning/10">
              <CardContent className="p-5">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Outstanding Receivables</p>
                <p className="text-2xl font-bold text-warning-text mt-1">
                  {isLoadingRecoveryKpis ? '…' : formatCurrency(recoveryKpis?.outstandingReceivables ?? 0)}
                </p>
              </CardContent>
            </Card>
            <Card className="border-primary/30 bg-primary/10">
              <CardContent className="p-5">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Realized BNPL Profit</p>
                <p className="text-2xl font-bold text-primary-text mt-1">
                  {isLoadingInstallmentProfit ? '…' : formatCurrency(installmentProfit?.realizedProfit ?? 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Expected {formatCurrency(installmentProfit?.expectedProfit ?? 0)} ({installmentProfit?.realizationRatePct ?? 0}%)
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Dead Stock Value (90d)</p>
                <p className="text-2xl font-bold mt-1">
                  {isLoadingDeadStock ? '…' : formatCurrency(deadStockReport?.totalDeadStockValue ?? 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">{deadStockReport?.count ?? 0} items</p>
              </CardContent>
            </Card>
          </div>

          {/* MEI-011 Top sales officers */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <UserCheck className="h-5 w-5" /> Top Sales Officers
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingRecoveryKpis ? (
                <SkeletonTable rows={5} cols={3} />
              ) : (recoveryKpis?.topSalesOfficers ?? []).length === 0 ? (
                <EmptyState icon={UserCheck} title="No sales data" subtitle="No completed sales found for the current period." />
              ) : (
                <div className="overflow-x-auto rounded-lg border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-4 py-2.5 text-start font-medium">Officer</th>
                        <th className="px-4 py-2.5 text-end font-medium">Sales Count</th>
                        <th className="px-4 py-2.5 text-end font-medium">Sales Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(recoveryKpis?.topSalesOfficers ?? []).map((o: TopSalesOfficerRow) => (
                        <tr key={o.userId} className="border-b hover:bg-accent/30 transition-colors">
                          <td className="px-4 py-2.5 font-medium">{o.name}</td>
                          <td className="px-4 py-2.5 text-end font-mono">{o.salesCount}</td>
                          <td className="px-4 py-2.5 text-end font-mono">{formatCurrency(o.salesTotal)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* MEI-010 Defaulter report */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-destructive-text" /> Defaulter Report
                </CardTitle>
                <span className="text-sm text-muted-foreground">
                  {defaulterReport?.count ?? 0} defaulters · {formatCurrency(defaulterReport?.totalOutstanding ?? 0)}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingDefaulters ? (
                <SkeletonTable rows={5} cols={4} />
              ) : (defaulterReport?.items ?? []).length === 0 ? (
                <EmptyState icon={AlertCircle} title="No defaulters" subtitle="No defaulted installment plans." />
              ) : (
                <div className="overflow-x-auto rounded-lg border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-4 py-2.5 text-start font-medium">Customer</th>
                        <th className="px-4 py-2.5 text-start font-medium">Invoice</th>
                        <th className="px-4 py-2.5 text-end font-medium">Overdue</th>
                        <th className="px-4 py-2.5 text-end font-medium">Remaining</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(defaulterReport?.items ?? []).map((d: DefaulterRow) => (
                        <tr key={d.planId} className="border-b hover:bg-accent/30 transition-colors">
                          <td className="px-4 py-2.5">
                            <div className="font-medium">{d.customerName ?? 'Walk-in'}</div>
                            {d.customerPhone && <div className="text-xs text-muted-foreground">{d.customerPhone}</div>}
                          </td>
                          <td className="px-4 py-2.5">{d.invoiceNumber ?? '—'}</td>
                          <td className="px-4 py-2.5 text-end font-mono">
                            {d.overdueCount} ({formatCurrency(d.overdueAmount)})
                          </td>
                          <td className="px-4 py-2.5 text-end font-mono font-semibold text-destructive-text">{formatCurrency(d.remainingAmount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* MEI-010 Outstanding balances */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-warning-text" /> Outstanding Balances
                </CardTitle>
                <span className="text-sm text-muted-foreground">
                  {outstandingReport?.count ?? 0} customers · {formatCurrency(outstandingReport?.totalOutstanding ?? 0)}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingOutstanding ? (
                <SkeletonTable rows={5} cols={3} />
              ) : (outstandingReport?.items ?? []).length === 0 ? (
                <EmptyState icon={DollarSign} title="No outstanding balances" subtitle="No active receivables." />
              ) : (
                <div className="overflow-x-auto rounded-lg border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-4 py-2.5 text-start font-medium">Customer</th>
                        <th className="px-4 py-2.5 text-end font-medium">Plans</th>
                        <th className="px-4 py-2.5 text-end font-medium">Outstanding</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(outstandingReport?.items ?? []).map((o: OutstandingRow, i: number) => (
                        <tr key={`${o.customerId ?? 'na'}-${i}`} className="border-b hover:bg-accent/30 transition-colors">
                          <td className="px-4 py-2.5">
                            <div className="font-medium">{o.customerName ?? 'Unassigned'}</div>
                            {o.customerPhone && <div className="text-xs text-muted-foreground">{o.customerPhone}</div>}
                          </td>
                          <td className="px-4 py-2.5 text-end font-mono">{o.planCount}</td>
                          <td className="px-4 py-2.5 text-end font-mono font-semibold">{formatCurrency(o.outstanding)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* MEI-010 Dead stock */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Boxes className="h-5 w-5" /> Dead Stock (no sales in 90 days)
                </CardTitle>
                <span className="text-sm text-muted-foreground">
                  {deadStockReport?.count ?? 0} items · {formatCurrency(deadStockReport?.totalDeadStockValue ?? 0)}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingDeadStock ? (
                <SkeletonTable rows={5} cols={4} />
              ) : (deadStockReport?.items ?? []).length === 0 ? (
                <EmptyState icon={Boxes} title="No dead stock" subtitle="All stocked products have sold recently." />
              ) : (
                <div className="overflow-x-auto rounded-lg border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-4 py-2.5 text-start font-medium">Product</th>
                        <th className="px-4 py-2.5 text-end font-medium">On Hand</th>
                        <th className="px-4 py-2.5 text-end font-medium">Stock Value</th>
                        <th className="px-4 py-2.5 text-start font-medium">Last Sold</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(deadStockReport?.items ?? []).map((p: DeadStockRow) => (
                        <tr key={p.productId} className="border-b hover:bg-accent/30 transition-colors">
                          <td className="px-4 py-2.5 font-medium">{p.productName}</td>
                          <td className="px-4 py-2.5 text-end font-mono">{p.stockOnHand}</td>
                          <td className="px-4 py-2.5 text-end font-mono">{formatCurrency(p.stockValue)}</td>
                          <td className="px-4 py-2.5 text-muted-foreground text-xs">
                            {p.lastSold ? formatDate(p.lastSold) : 'Never'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function PnlRow({ label, value, color, bold }: { label: string; value: number; color?: string; bold?: boolean }) {
  return (
    <div className={`flex justify-between text-sm ${bold ? 'font-bold' : ''}`}>
      <span>{label}</span>
      <span className={`font-mono ${color || ''}`}>{formatCurrency(value)}</span>
    </div>
  );
}
